import React, { useState, useEffect, useRef, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { getUserConfig } from '../../services/config';
import { analyzeSegment, createSegment } from '../../services/chatService';
import { useNotification } from '../../contexts/NotificationContext';
import segmentService from '../../services/segmentService';
import Joyride, { STATUS, EVENTS } from 'react-joyride';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  InputAdornment,
  CircularProgress,
  IconButton,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tab,
  Tabs,
  TabPanel,
  Pagination,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import Tooltip from '@mui/material/Tooltip';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CodeIcon from '@mui/icons-material/Code';
import DescriptionIcon from '@mui/icons-material/Description';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GroupIcon from '@mui/icons-material/Group';

import ListItemIcon from '@mui/material/ListItemIcon';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AlertCircleIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { getFavorites, addFavorite, removeFavorite } from '../../services/favoriteService';
import { getArchives, addArchive, removeArchive } from '../../services/archiveService';
import MenuItem from '@mui/material/MenuItem';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Menu from '@mui/material/Menu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Axios client for backend API (auth via HttpOnly cookies)
import api from '../../services/auth';

/**
 * SECURITY IMPROVEMENT: All segment operations now go through secure backend endpoints
 * instead of direct Unomi API calls. This removes the need for frontend access to
 * Unomi credentials and ensures proper authentication and authorization.
 */

/**
 * Performance Optimizations Applied:
 * 1. Campaign data caching (5-minute cache) to avoid re-fetching all campaigns on each segment selection
 * 2. Request debouncing (300ms) for fast segment switching
 * 3. Lazy loading of segment details - only fetched when Configuration tab is accessed
 * 4. Request tracking to prevent race conditions and redundant requests
 * 5. Proper request cancellation with AbortController
 */
const Segment = () => {
  const [segments, setSegments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  
  const [sortField, setSortField] = useState('created'); // created | name | profiles
  const [sortDirection, setSortDirection] = useState('desc'); // asc | desc
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileCounts, setProfileCounts] = useState({});
  const [profileSortingPending, setProfileSortingPending] = useState(false);
  // Removed highlightError state - now using simple search population
  const [tabValue, setTabValue] = useState(0);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSegments, setTotalSegments] = useState(0);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [allBrandSegments, setAllBrandSegments] = useState([]); // Store all brand segments
  // Items per page state and options
  const [pageSize, setPageSize] = useState(10);
  const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 50, 100];

  // State pour l'analyse IA
  const [analysisResult, setAnalysisResult] = useState(null);
  // Full JSON of the currently selected segment (fetched on demand)
  const [detailedSegment, setDetailedSegment] = useState(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisSummary, setAnalysisSummary] = useState(null);
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const analysisTextRef = useRef(null);
  
  // Coordination state for sequential processing
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [shouldLoadCampaigns, setShouldLoadCampaigns] = useState(false);

  const [favorites, setFavorites] = useState([]);
  const [archivedItems, setArchivedItems] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [favoritesError, setFavoritesError] = useState(null);
  const [favoritesRetryCount, setFavoritesRetryCount] = useState(0);
  const [favoritesInitialized, setFavoritesInitialized] = useState(false);

  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuOpen, setActionMenuOpen] = useState(false);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editSegmentName, setEditSegmentName] = useState('');
  const [editSegmentDescription, setEditSegmentDescription] = useState('');
  // Inline edit panel states
  const [isEditing, setIsEditing] = useState(false);
  const [editSegmentRules, setEditSegmentRules] = useState(''); // Natural language rules
  const [generatedDefinition, setGeneratedDefinition] = useState(''); // JSON definition preview
  const [isDefinitionOpen, setIsDefinitionOpen] = useState(false);
  // Loading states for save operation
  const [isSaving, setIsSaving] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  
  // Delete confirmation state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [segmentToDelete, setSegmentToDelete] = useState(null);
  const [isDeletingSegment, setIsDeletingSegment] = useState(false);
  
  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneError, setCloneError] = useState('');
  const [cloneLoading, setCloneLoading] = useState(false);
  
  // Campaign usage state with caching
  const [segmentCampaigns, setSegmentCampaigns] = useState([]);
  const [segmentCampaignsLoading, setSegmentCampaignsLoading] = useState(false);
  const [segmentCampaignsError, setSegmentCampaignsError] = useState(null);
  const [allCampaignsCache, setAllCampaignsCache] = useState(null); // Cache all campaigns
  const [campaignsCacheTimestamp, setCampaignsCacheTimestamp] = useState(null);
  const CAMPAIGNS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

  // Filter sidebar state
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  
  // Tour state
  const [runTour, setRunTour] = useState(false);

  // Performance optimization: Request debouncing and deduplication
  const segmentSelectionTimeoutRef = useRef(null);
  const currentRequestsRef = useRef(new Set());
  const activeProfileCountRequests = useRef(new Set());
  
  // Global process cancellation management
  const globalAbortController = useRef(null);
  const activeCampaignRequests = useRef(new Set());

  const sidebarFilters = [
    { id: 'all', label: 'Tous les segments', icon: <GroupIcon /> },
    { id: 'favorites', label: 'Favoris', icon: <StarBorderIcon /> },
    { id: 'archived', label: 'Archivés', icon: <ArchiveIcon /> },
  ];

  const { showNotification } = useNotification();

  // Enhanced favorites loading with proper error handling and retry mechanism
  const loadFavoritesAndArchives = useCallback(async (retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s
    
    console.log(`🔄 Loading favorites and archives (attempt ${retryCount + 1}/${maxRetries + 1})`);
    setFavoritesLoading(true);
    setFavoritesError(null);
    
    try {
      const [favs, archives] = await Promise.all([
        getFavorites('segment'),
        getArchives('segment')
      ]);
      
      console.log(`✅ Successfully loaded ${favs?.length || 0} favorites and ${archives?.length || 0} archives`);
      
      setFavorites(favs || []);
      setArchivedItems(archives || []);
      setFavoritesInitialized(true);
      setFavoritesError(null);
      setFavoritesRetryCount(0);
      
      return { favs: favs || [], archives: archives || [] };
      
    } catch (error) {
      console.error(`❌ Error loading favorites/archives (attempt ${retryCount + 1}):`, error);
      
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to load favorites';
      
      if (retryCount < maxRetries) {
        console.log(`⏳ Retrying in ${retryDelay}ms...`);
        setFavoritesRetryCount(retryCount + 1);
        
        // Retry after delay
        setTimeout(() => {
          loadFavoritesAndArchives(retryCount + 1);
        }, retryDelay);
        
        return { favs: [], archives: [] };
      } else {
        console.error(`❌ Failed to load favorites after ${maxRetries + 1} attempts`);
        setFavoritesError(`Impossible de charger les favoris: ${errorMessage}`);
        setFavoritesInitialized(true); // Mark as initialized even on failure to prevent infinite retries
        
        // Show user notification
        setNotification({
          open: true,
          message: `Erreur lors du chargement des favoris: ${errorMessage}`,
          severity: 'warning'
        });
        
        return { favs: [], archives: [] };
      }
    } finally {
      setFavoritesLoading(false);
    }
  }, []);
  
  // Load favorites and archives independently on component mount
  useEffect(() => {
    if (!favoritesInitialized) {
      console.log('🚀 Component mounted - loading favorites and archives independently');
      loadFavoritesAndArchives();
    }
  }, [loadFavoritesAndArchives, favoritesInitialized]);

  // Helper functions for favorites and archives
  const isFavorite = useCallback((segmentId) => {
    if (!favoritesInitialized) {
      return false; // Don't show favorite state until loaded
    }
    return favorites.some(fav => fav.data_id === segmentId);
  }, [favorites, favoritesInitialized]);

  const isArchived = useCallback((segmentId) => {
    // If we're currently viewing archived segments, all segments in the list are archived
    if (activeFilter === 'archived') {
      return true;
    }
    // Otherwise, check the archivedItems state
    return archivedItems.some(arch => arch.data_id === segmentId);
  }, [archivedItems, activeFilter]);

  // Keep search input synchronized with applied search
  useEffect(() => {
    setSearchInput(search || "");
  }, [search]);

  const handleApplySearch = () => {
    // Apply search only on validation (button/Enter)
    setSearch(searchInput.trim());
  };

  // Auto-search with debounce when searchInput changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 300); // 300ms debounce for auto-search

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Function to construct query params similar to campaigns
  const buildRequestParams = (page, size, searchTerm, filter) => {
    const params = new URLSearchParams({
      offset: ((page - 1) * size).toString(),
      size: size.toString()
    });
    
    // Add sorting parameters
    if (sortField && sortDirection) {
      params.append('sort_field', sortField);
      params.append('sort_direction', sortDirection);
    }
    
    if (searchTerm) {
      params.append('search', searchTerm);
    }
    
    // Add filter-specific parameters
    switch (filter) {
      case 'favorites':
        params.append('favorites_only', 'true');
        break;
      case 'archived':
        params.append('archived_only', 'true');
        break;
      default:
        // 'all' - no additional filtering
        break;
    }
    
    return params;
  };

  // Server-side segments fetch with pagination
  const fetchSegments = async (page, size, searchTerm, filter) => {
    return fetchSegmentsWithDependencies(page, size, searchTerm, filter, favorites, archivedItems);
  };

  // Server-side segments fetch with pagination and explicit favorites/archives lists
  const fetchSegmentsWithFavorites = async (page, size, searchTerm, filter, favoritesList) => {
    return fetchSegmentsWithDependencies(page, size, searchTerm, filter, favoritesList, archivedItems);
  };

  // Server-side segments fetch with pagination using secure backend endpoints
  const fetchSegmentsWithDependencies = async (page, size, searchTerm, filter, favoritesList, archivedList) => {
    try {
      console.log(`Fetching segments: page=${page}, size=${size}, search=${searchTerm}, filter=${filter}`);
      
              // Use the secure backend service instead of direct Unomi calls
        console.log(`Fetching segments with filter: ${filter}, favorites: ${filter === 'favorites'}, archived: ${filter === 'archived'}`);
        
        const result = await segmentService.getSegmentsPaginated({
          page: page,
          pageSize: size,
          search: searchTerm,
          sortField: sortField,
          sortDirection: sortDirection,
          favoritesOnly: filter === 'favorites',
          archivedOnly: filter === 'archived'
        });
        
        console.log(`Backend returned ${result.segments?.length || 0} segments for filter '${filter}'`);
      
      // Extract segments from result
      const segments = result.segments;
      
      console.log(`Retrieved ${segments.length} segments from backend`);
      
      // If we got profile counts from the backend (when sorting by profiles), update state
      if (result.profileCounts) {
        console.log(`Received ${Object.keys(result.profileCounts).length} profile counts from backend`);
        setProfileCounts(prev => ({ ...prev, ...result.profileCounts }));
      }
      
      return {
        segments: segments,
        total: result.total,
        fullList: segments, // For now, return the paginated segments as full list
        hasProfileCounts: !!result.profileCounts // Indicate if we received profile counts
      };
    } catch (error) {
      console.error('Error fetching segments:', error);
      throw error;
    }
  };

  // Optimized function to refresh profile counts using batch API with deduplication
  const refreshProfileCounts = useCallback(async (segmentList) => {
    try {
      if (!segmentList || segmentList.length === 0) {
        return;
      }

      // Extract segment IDs and create a unique key for this request
      const segmentIds = segmentList.map(seg => seg.id).sort();
      const requestKey = segmentIds.join(',');
      
      // Check if this exact request is already in progress
      if (activeProfileCountRequests.current.has(requestKey)) {
        console.log(`⏸️ Skipping duplicate profile count request for ${segmentIds.length} segments`);
        return;
      }
      
      // Mark this request as active
      activeProfileCountRequests.current.add(requestKey);

      console.log(`Refreshing profile counts for ${segmentList.length} segments using batch API`);
      console.log(`Fetching missing profile counts for ${segmentList.length} segments`);
      
      try {
        // Use batch API to get all counts at once
        const counts = await segmentService.getBatchProfileCounts(segmentIds);
        
        // Update state with all counts
        setProfileCounts(prev => ({ ...prev, ...counts }));
        
        console.log(`Batch profile counts completed: ${Object.keys(counts).length} total counts`);
        console.log(`Updated profile counts for ${Object.keys(counts).length} segments`);
      } finally {
        // Always remove the request from active set
        activeProfileCountRequests.current.delete(requestKey);
      }
    } catch (error) {
      console.error('Error refreshing profile counts:', error);
      // Set error counts to 0 to avoid UI issues
      const errorCounts = {};
      segmentList.forEach(seg => {
        errorCounts[seg.id] = 0;
      });
      setProfileCounts(prev => ({ ...prev, ...errorCounts }));
      
      // Remove from active requests on error
      const segmentIds = segmentList.map(seg => seg.id).sort();
      const requestKey = segmentIds.join(',');
      activeProfileCountRequests.current.delete(requestKey);
    }
  }, []);

  // Helper to refresh profile counts for a given list
  const refreshProfileCountsLegacy = async (segmentList) => {
    const countResults = {};
    await Promise.all(
      segmentList.map(async (seg) => {
        try {
          const cnt = await fetchProfileCount(seg.id);
          countResults[seg.id] = cnt;
        } catch (e) {
          console.error('Erreur comptage profils pour', seg.id, e);
          countResults[seg.id] = 'Erreur';
        }
      })
    );
    setProfileCounts(prev => ({ ...prev, ...countResults }));
  };

  // Helper function to find a segment by ID (current page + cache)
  const findSegmentById = useCallback((segmentId) => {
    if (!segmentId) return null;
    
    // First search in current page
    let segment = segments.find(s => s.id === segmentId);
    
    // If not found, search in cache
    if (!segment) {
      segment = allBrandSegments.find(s => s.id === segmentId);
    }
    
    return segment;
  }, [segments, allBrandSegments]);

  const handleFilterChange = (filterId) => {
    // Check if favorites are loaded when switching to favorites filter
    if (filterId === 'favorites' && (!favoritesInitialized || favoritesError)) {
      setNotification({
        open: true,
        message: favoritesError ? 'Erreur lors du chargement des favoris. Veuillez réessayer.' : 'Chargement des favoris en cours...',
        severity: favoritesError ? 'error' : 'info'
      });
      
      // If there's an error, try to reload favorites
      if (favoritesError) {
        setFavoritesInitialized(false);
        setFavoritesRetryCount(0);
      }
      return;
    }
    
    setActiveFilter(filterId);
    setCurrentPage(1); // Reset to first page when filter changes
    setSelected(null); // Clear selection when filter changes
  };

  // Handle page-size change
  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    setSelected(null); // Clear selection when page size changes
    // The useEffect will trigger re-fetch with new page size
  };

  // Main page change handler - optimized for performance
  const handlePageChange = async (event, newPage) => {
    if (newPage === currentPage) return;
    
    // Cancel all ongoing processes when changing pages
    cancelAllOngoingProcesses('Page change').catch(console.warn);
    
    if (event && searchParams.has('name')) {
      setSearchParams({}, { replace: true });
    }
    
    setPaginationLoading(true);
    setSelected(null); // Clear selection when changing pages
    
    // Set pending state if we're sorting by profiles
    if (sortField === 'profiles') {
      setProfileSortingPending(true);
    }
    
    try {
      const result = await fetchSegments(newPage, pageSize, search, activeFilter);
      
      setSegments(result.segments);
      setTotalSegments(result.total);
      setCurrentPage(newPage);
      
      // Update cache with new segments
      setAllBrandSegments(result.fullList);
      
      // Fetch profile counts for new page segments using optimized batch API
      // Only fetch if we didn't already get them from the backend (when sorting by profiles)
      if (result.segments.length > 0 && !result.hasProfileCounts) {
        await refreshProfileCounts(result.segments);
      }
      
      // Clear pending state
      setProfileSortingPending(false);
      
    } catch (err) {
      console.error('Error changing page:', err);
      setError('Error changing page.');
    } finally {
      setPaginationLoading(false);
      setProfileSortingPending(false);
    }
  };

  // Global process cancellation function
  const cancelAllOngoingProcesses = async (reason = 'New process starting') => {
    console.log(`🛑 Cancelling all ongoing processes: ${reason}`);
    
    // Cancel global abort controller
    if (globalAbortController.current) {
      globalAbortController.current.abort();
      globalAbortController.current = null;
    }
    
    // Clear analysis requests
    currentRequestsRef.current.clear();
    
    // Clear campaign requests
    activeCampaignRequests.current.clear();
    
    // Clear timeouts
    if (segmentSelectionTimeoutRef.current) {
      clearTimeout(segmentSelectionTimeoutRef.current);
      segmentSelectionTimeoutRef.current = null;
    }
    
    // Signal backend to cancel all requests for this user
    try {
      await api.post('/segments/cancel-requests', {
        reason: reason
      });
      console.log('✅ Backend cancellation signal sent');
    } catch (error) {
      console.warn('⚠️ Failed to send backend cancellation signal:', error.message);
      // Don't throw error - frontend cancellation should still work
    }
    
    // Reset loading states
    setIsAnalysisLoading(false);
    setSegmentCampaignsLoading(false);
    setAnalysisCompleted(false);
    setShouldLoadCampaigns(false);
    
    console.log('✅ All processes cancelled and states reset');
  };

  // Function to get profile count for a segment using secure backend endpoint
  const fetchProfileCount = async (segmentId) => {
    try {
      console.log(`Fetching profile count for segment: ${segmentId}`);
      const count = await segmentService.getSegmentProfileCount(segmentId);
      return count || 0;
    } catch (error) {
      console.error('Error fetching profile count:', error);
      return 0; // Return 0 on error to avoid UI issues
    }
  };

  // Optimized analysis loading with request tracking
  useEffect(() => {
    // Cancel all ongoing processes when segment selection changes
    const cancelAndStart = async () => {
      await cancelAllOngoingProcesses('Segment selection changed');
      
      // Create new global abort controller for this selection
      globalAbortController.current = new AbortController();
      const controller = globalAbortController.current;
      const requestId = Date.now().toString();
      
      // Clear previous state
      setAnalysisResult(null);
      setAnalysisError(null);
      setAnalysisSummary(null);
      setIsAnalysisExpanded(false);
      setAnalysisCompleted(false);
      setShouldLoadCampaigns(false);

      if (selected) {

        // Debounce segment selection for fast switching
        segmentSelectionTimeoutRef.current = setTimeout(() => {
        const segment = paginatedSegments.find(s => s.id === selected) || allBrandSegments.find(s => s.id === selected);
        if (segment && segment.name) {
          const segmentName = segment.name;
          const segmentId = segment.id;
          
          const runAnalysis = async (signal) => {
            // Track this request
            currentRequestsRef.current.add(requestId);
            setIsAnalysisLoading(true);
            
            try {
              // First try to get precomputed analysis from database (fast)
              console.log(`Attempting to fetch precomputed analysis for segment: ${segmentId}`);
              // Use the segmentService method for better consistency and error handling
              const precomputedData = await segmentService.getPrecomputedAnalysis(segmentId, signal);
              
              if (precomputedData && precomputedData.success) {
                console.log('Using precomputed analysis from database');
                
                // Check if the precomputed analysis contains a quota error
                if (precomputedData.plain_text_analysis && precomputedData.plain_text_analysis.includes("⚠️ Limite de quota API atteinte")) {
                  setAnalysisError(precomputedData.plain_text_analysis);
                  setAnalysisResult(null);
                  setAnalysisSummary(null);
                } else if (precomputedData.plain_text_analysis && precomputedData.plain_text_analysis.toLowerCase().includes("quota exceeded")) {
                  // Fallback for raw quota errors that weren't properly formatted
                  setAnalysisError("⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.");
                  setAnalysisResult(null);
                  setAnalysisSummary(null);
                } else {
                  setAnalysisResult(precomputedData.plain_text_analysis || null);
                  setAnalysisSummary(precomputedData.profile_summary || null);
                  
                  if (!precomputedData.plain_text_analysis && !precomputedData.profile_summary) {
                    setAnalysisError("L'analyse précompilée ne contient aucun contenu.");
                  }
                }
                
                // CRITICAL: Signal analysis completion immediately when precomputed data is found
                console.log('🎯 Precomputed analysis completed, triggering campaign loading...');
                if (currentRequestsRef.current.has(requestId) && !signal.aborted) {
                  setIsAnalysisLoading(false);
                  setAnalysisCompleted(true);
                  setShouldLoadCampaigns(true);
                }
                currentRequestsRef.current.delete(requestId);
                return; // Exit early since we have precomputed data
              } else {
                throw new Error('No precomputed analysis available');
              }
            } catch (precomputedError) {
              // Handle cancellation first
              if (precomputedError.name === 'AbortError' || precomputedError.code === 'ERR_CANCELED') {
                console.log('Precomputed analysis was cancelled');
                return; // Exit early for cancelled requests
              }
              
              // Handle different types of precomputed analysis errors
              if (precomputedError.message === 'TIMEOUT_FALLBACK') {
                // Timeout is expected behavior, not an error - just fall back to live analysis
                console.log('Precomputed analysis timeout - proceeding with live analysis');
              } else if (precomputedError.message.includes("⚠️ Limite de quota API atteinte")) {
                // Handle quota errors from precomputed analysis
                setAnalysisError(precomputedError.message);
                setAnalysisResult(null);
                setAnalysisSummary(null);
                
                // Even with quota error, signal completion so campaigns can load
                console.log('⚠️ Quota error in precomputed analysis, still triggering campaign loading...');
                if (currentRequestsRef.current.has(requestId) && !signal.aborted) {
                  setIsAnalysisLoading(false);
                  setAnalysisCompleted(true);
                  setShouldLoadCampaigns(true);
                }
                currentRequestsRef.current.delete(requestId);
                return; // Don't try live analysis if we have a quota error
              } else {
                // Other errors - log them for debugging but still fall back to live analysis
                console.log('Precomputed analysis not available, falling back to live analysis');
                console.log('Precomputed error:', precomputedError.message);
              }
              
              try {
                const fullQuery = `Fournis une analyse détaillée du segment nommé \"${segmentName}\".`;
                // Use the imported analyzeSegment function from chatService
                const data = await analyzeSegment(fullQuery);
                
                // Check if the response contains a quota error
                if (data.plain_text_analysis && data.plain_text_analysis.includes("⚠️ Limite de quota API atteinte")) {
                  setAnalysisError(data.plain_text_analysis);
                  setAnalysisResult(null);
                  setAnalysisSummary(null);
                } else if (data.plain_text_analysis && data.plain_text_analysis.toLowerCase().includes("quota exceeded")) {
                  // Fallback for raw quota errors that weren't properly formatted
                  setAnalysisError("⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.");
                  setAnalysisResult(null);
                  setAnalysisSummary(null);
                } else if (!data.success) {
                  // Handle other error cases
                  setAnalysisError(data.message || "Une erreur est survenue lors de l'analyse.");
                  setAnalysisResult(null);
                  setAnalysisSummary(null);
                } else {
                  setAnalysisResult(data.plain_text_analysis || null);
                  setAnalysisSummary(data.profile_summary || null);
                  
                  if (!data.plain_text_analysis && !data.profile_summary) {
                    setAnalysisError("L'analyse a réussi mais n'a renvoyé aucun contenu.");
                  }
                  
                  // CRITICAL: Signal analysis completion immediately when live analysis succeeds
                  console.log('🎯 Live analysis completed, triggering campaign loading...');
                  if (currentRequestsRef.current.has(requestId) && !signal.aborted) {
                    setIsAnalysisLoading(false);
                    setAnalysisCompleted(true);
                    setShouldLoadCampaigns(true);
                  }
                  currentRequestsRef.current.delete(requestId);
                  return; // Exit early since analysis succeeded
                }
              } catch (liveError) {
                if (liveError.name === 'AbortError' || liveError.code === 'ERR_CANCELED') {
                  console.log('Live analysis was cancelled');
                  return; // Exit early for cancelled requests
                }
                
                if (liveError.name !== 'AbortError') {
                  const errorMessage = liveError.detail || liveError.message || "Une erreur inconnue est survenue.";
                  
                  // Check if this is a quota error in the exception message
                  if (errorMessage.includes("⚠️ Limite de quota API atteinte") || 
                      errorMessage.toLowerCase().includes("quota") ||
                      errorMessage.toLowerCase().includes("429")) {
                    setAnalysisError("⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.");
                  } else {
                    setAnalysisError(errorMessage);
                  }
                  
                  // Even if analysis fails, we still want to load campaigns after a brief delay
                  console.log('⚠️ Analysis failed, will still load campaigns after brief delay...');
                  
                  // Signal completion even on error so campaigns can load
                  if (currentRequestsRef.current.has(requestId) && !signal.aborted) {
                    setIsAnalysisLoading(false);
                    setAnalysisCompleted(true);
                    setShouldLoadCampaigns(true);
                  }
                  currentRequestsRef.current.delete(requestId);
                  return; // Exit early to prevent double completion in finally block
                }
              }
            } finally {
              // Only update loading state if this is still the current request AND we haven't already handled completion
              if (currentRequestsRef.current.has(requestId) && !signal.aborted) {
                setIsAnalysisLoading(false);
                setAnalysisCompleted(true);
                // Signal that campaigns can now be loaded
                setShouldLoadCampaigns(true);
                console.log('🎯 Analysis completed (fallback path), triggering campaign loading...');
                currentRequestsRef.current.delete(requestId);
              }
            }
          };
          
          runAnalysis(controller.signal);
        }
        }, 300); // 300ms debounce for fast segment switching
      }
    };
    
    // Start the async operation
    cancelAndStart();

    return () => {
      if (globalAbortController.current) {
        globalAbortController.current.abort();
        globalAbortController.current = null;
      }
      currentRequestsRef.current.clear();
      if (segmentSelectionTimeoutRef.current) {
        clearTimeout(segmentSelectionTimeoutRef.current);
      }
    };
  }, [selected]);

  // Manual refresh analysis function
  const handleRefreshAnalysis = async () => {
    if (!selected || isAnalysisLoading) return;
    
    const segment = paginatedSegments.find(s => s.id === selected) || allBrandSegments.find(s => s.id === selected);
    if (!segment || !segment.name) return;

    // Clear previous results
    setAnalysisResult(null);
    setAnalysisError(null);
    setAnalysisSummary(null);
    setIsAnalysisExpanded(false);
    setIsAnalysisLoading(true);

    const segmentName = segment.name;
    const fullQuery = `Fournis une analyse détaillée du segment nommé \"${segmentName}\".`;
    
    try {
      // Always use live analysis for refresh to get the latest data
      console.log('Manual refresh: using live analysis for latest data');
      // Use the imported analyzeSegment function from chatService
      const data = await analyzeSegment(fullQuery);
      
      // Check if the response contains a quota error
      if (data.plain_text_analysis && data.plain_text_analysis.includes("⚠️ Limite de quota API atteinte")) {
        setAnalysisError(data.plain_text_analysis);
        setAnalysisResult(null);
        setAnalysisSummary(null);
      } else if (data.plain_text_analysis && data.plain_text_analysis.toLowerCase().includes("quota exceeded")) {
        // Fallback for raw quota errors that weren't properly formatted
        setAnalysisError("⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.");
        setAnalysisResult(null);
        setAnalysisSummary(null);
      } else if (!data.success) {
        // Handle other error cases
        setAnalysisError(data.message || "Une erreur est survenue lors de l'analyse.");
        setAnalysisResult(null);
        setAnalysisSummary(null);
      } else {
        setAnalysisResult(data.plain_text_analysis || null);
        setAnalysisSummary(data.profile_summary || null);
        if (!data.plain_text_analysis && !data.profile_summary) {
          setAnalysisError("L'analyse a réussi mais n'a renvoyé aucun contenu.");
        }
      }
    } catch (error) {
      const errorMessage = error.detail || error.message || "Une erreur inconnue est survenue.";
      
      // Check if this is a quota error in the exception message
      if (errorMessage.includes("⚠️ Limite de quota API atteinte") || 
          errorMessage.toLowerCase().includes("quota") ||
          errorMessage.toLowerCase().includes("429")) {
        setAnalysisError("⚠️ Limite de quota API atteinte. Votre quota d'utilisation de l'API Google Gemini a été dépassé. Veuillez vérifier votre plan et vos détails de facturation, ou réessayer plus tard.");
      } else {
        setAnalysisError(errorMessage);
      }
    } finally {
      setIsAnalysisLoading(false);
    }
  };

  useLayoutEffect(() => {
    const timer = setTimeout(() => {
      if (analysisTextRef.current) {
        const isOverflowing = analysisTextRef.current.scrollHeight > analysisTextRef.current.clientHeight;
        setIsExpandable(isOverflowing);
      } else {
        setIsExpandable(false);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [analysisResult, isAnalysisLoading]);

  // Consolidated data fetching with debounce and request deduplication
  useEffect(() => {
    let isCancelled = false;
    let timeoutId = null;

    const fetchData = async (isInitial = false) => {
      if (isCancelled) return;

      // Use different loading states for initial vs subsequent loads
      if (isInitial) {
        setLoading(true);
      } else {
        setPaginationLoading(true);
      }
      setError(null);
      
      // Set pending state if we're sorting by profiles
      if (sortField === 'profiles') {
        setProfileSortingPending(true);
      }
      
      try {
        // Use already loaded favorites and archives (loaded independently)
        let favs = favorites;
        let archives = archivedItems;
        
        // For initial load, wait for favorites to be loaded if they're still loading
        if (isInitial && !favoritesInitialized && favoritesLoading) {
          console.log('⏳ Waiting for favorites to load before proceeding with initial segment fetch...');
          // Wait up to 5 seconds for favorites to load
          let waitTime = 0;
          const maxWait = 5000; // 5 seconds
          const checkInterval = 100; // Check every 100ms
          
          while (!favoritesInitialized && waitTime < maxWait && !isCancelled) {
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            waitTime += checkInterval;
          }
          
          if (!favoritesInitialized) {
            console.warn('⚠️ Favorites loading timed out, proceeding without them');
          } else {
            console.log('✅ Favorites loaded, proceeding with segment fetch');
            // Update with current favorites and archives
            favs = favorites;
            archives = archivedItems;
          }
        }
        
        // Clear selection during non-initial loads (search/filter changes)
        if (!isInitial) {
          setSelected(null);
        }
        
        // Fetch segments using optimized backend API
        const result = await fetchSegmentsWithDependencies(1, pageSize, search, activeFilter, favs, archives);
        
        if (isCancelled) return;

        const fullSegmentList = result.fullList;
        
        setAllBrandSegments(fullSegmentList);
        setTotalSegments(result.total);

        let pageToLoad = 1;
        let segmentToSelect = null;

        // No auto-selection from URL parameters - just load normally

        const paginatedData = fullSegmentList.slice((pageToLoad - 1) * pageSize, pageToLoad * pageSize);
        setSegments(paginatedData);
        setCurrentPage(pageToLoad);
        
        if (segmentToSelect) {
          setSelected(segmentToSelect);
        }

        // Fetch profile counts for the segments on the loaded page using batch API
        // Only fetch if we didn't already get them from the backend (when sorting by profiles)
        if (paginatedData.length > 0 && !result.hasProfileCounts && !isCancelled) {
          await refreshProfileCounts(paginatedData);
        }
        
        setProfileSortingPending(false);

      } catch (err) {
        if (!isCancelled) {
          console.error('Error fetching data:', err);
          setError('Error fetching segments.');
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          setPaginationLoading(false);
          setProfileSortingPending(false);
        }
      }
    };

    // Determine if this is an initial load or a subsequent change
    const isInitialLoad = !segments.length && !loading && !paginationLoading;
    
    if (isInitialLoad) {
      // Initial load - no debounce
      console.log('🔄 Initial data load triggered');
      fetchData(true);
    } else {
      // Subsequent changes - debounce for 300ms
      console.log('🔄 Debounced data fetch scheduled');
      timeoutId = setTimeout(() => {
        console.log('🔄 Debounced data fetch executing');
        fetchData(false);
      }, 300);
    }

    // Cleanup function
    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [pageSize, activeFilter, sortField, sortDirection, search]); // Single dependency array
  
  // Note: Profile count sorting is now handled server-side in the backend
  // This improves performance and ensures proper sorting across all pages

  // Removed complex findAndHighlightSegment function - now using simple search population

  // Handle state-based navigation from other components (chat, campaigns)
  useEffect(() => {
    if (location.state?.searchTerm && !loading) {
      const searchTerm = location.state.searchTerm;
      const source = location.state.source || 'unknown';
      
      console.log(`🔗 State-based navigation detected from ${source}: searchTerm=${searchTerm}`);
      
      // Populate the search bar with the search term
      setSearchInput(searchTerm);
      setSearch(searchTerm);
      
      // Clear the state to prevent re-triggering on subsequent renders
      // Replace the current history entry without the state
      navigate(location.pathname, { replace: true });
      
      console.log(`✅ Populated search with: "${searchTerm}" from ${source} and cleared state`);
    }
  }, [location.state, loading, navigate, location.pathname]);

  // Handle URL parameters - simple search population only (for backward compatibility)
  useEffect(() => {
    const highlightName = searchParams.get('name');

    if (!highlightName) {
      return;
    }
    
    if (loading) return;

    console.log(`🔗 URL parameter detected (legacy): name=${highlightName}`);

    // Simply populate the search bar and clear the URL parameter
    const decodedName = decodeURIComponent(highlightName);
    setSearchInput(decodedName);
    setSearch(decodedName);
    
    // Clear the URL parameter immediately to prevent re-triggering
    setSearchParams({}, { replace: true });
    
    console.log(`✅ Populated search with: "${decodedName}" and cleared URL parameter`);
    
  }, [searchParams, loading, setSearchParams]);

  // Removed unused useEffect for highlighting - now using simple search population

  const handleDeleteSegment = async (segmentId) => {
    try {
      console.log(`🗑️ Starting deletion of segment: ${segmentId}`);
      
      // Check if actions are disabled (loading or in use)
      if (isSegmentActionsDisabled(segmentId)) {
        if (segmentId === selected && isAnalysisLoading) {
          setNotification({ 
            open: true, 
            message: "Veuillez patienter pendant l'analyse Qilinsa Amplify...", 
            severity: 'info' 
          });
        } else if (segmentCampaignsLoading && segmentId === selected) {
          setNotification({ 
            open: true, 
            message: 'Veuillez patienter pendant le chargement des informations de campagne...', 
            severity: 'info' 
          });
        } else if (isSegmentInUse(segmentId)) {
          setNotification({ 
            open: true, 
            message: 'Impossible de supprimer ce segment car il est utilisé dans des campagnes actives', 
            severity: 'warning' 
          });
        }
        return;
      }

      // Find the segment to get its display name before deletion
      let segmentName = null;
      const segmentToDelete = [...segments, ...allBrandSegments].find(seg => 
        String(seg.id) === String(segmentId) || 
        String(seg.itemId) === String(segmentId)
      );
      
      if (segmentToDelete) {
        // Extract the display name from various possible locations
        segmentName = segmentToDelete.name || 
                     segmentToDelete.metadata?.name || 
                     segmentToDelete.itemId || 
                     segmentId;
        console.log(`📋 Found segment name for deletion sync: "${segmentName}"`);
      } else {
        console.warn(`⚠️ Could not find segment ${segmentId} in current lists for name extraction`);
        // Try to get the segment details from backend as fallback
        try {
          const segmentDetails = await segmentService.getSegmentById(segmentId);
          segmentName = segmentDetails.name || 
                       segmentDetails.metadata?.name || 
                       segmentDetails.itemId || 
                       segmentId;
          console.log(`📋 Retrieved segment name from backend: "${segmentName}"`);
        } catch (fetchError) {
          console.warn(`⚠️ Could not fetch segment details for ${segmentId}:`, fetchError);
          segmentName = segmentId; // Fallback to using the ID
        }
      }

      console.log(`🔄 Calling segmentService.deleteSegment for: ${segmentId}`);

      // Delete using secure backend endpoint
      const result = await segmentService.deleteSegment(segmentId);
      
      console.log(`📝 Delete segment result:`, result);
      console.log(`✅ Delete success status: ${result?.success}`);
      console.log(`📨 Delete message: ${result?.message}`);
      
      // Check if deletion was successful
      if (!result || result.success !== true) {
        const errorMessage = result?.message || 'Failed to delete segment - unknown error';
        console.error(`❌ Deletion failed for ${segmentId}:`, errorMessage);
        throw new Error(errorMessage);
      }

      console.log(`✅ Segment ${segmentId} deleted successfully from Unomi, now syncing with Mautic...`);

      // Sync deletion with Mautic using the segment name
      if (segmentName && segmentName !== segmentId) {
        try {
          console.log(`🔄 Syncing segment deletion with Mautic: "${segmentName}"`);
          const syncResult = await segmentService.syncSegmentDeletions([segmentName]);
          
          if (syncResult.success) {
            const summary = syncResult.summary || {};
            if (summary.successful_deletions > 0) {
              console.log(`✅ Successfully synced deletion with Mautic: ${summary.successful_deletions} segments deleted`);
            } else if (summary.not_found > 0) {
              console.log(`ℹ️ Segment "${segmentName}" was not found in Mautic (already deleted or never existed)`);
            }
          } else {
            console.warn(`⚠️ Mautic sync failed: ${syncResult.message}`);
          }
        } catch (syncError) {
          console.warn(`⚠️ Failed to sync deletion with Mautic for "${segmentName}":`, syncError);
          // Don't fail the entire deletion if Mautic sync fails
        }
      } else {
        console.log(`ℹ️ Skipping Mautic sync (using segment ID as name): ${segmentName}`);
      }

      console.log(`✅ Segment ${segmentId} deletion process completed, updating UI state...`);

      // Remove the deleted segment from the current lists
      setSegments(prev => {
        const filtered = prev.filter(seg => seg.id !== segmentId);
        console.log(`📋 Updated segments list: ${prev.length} -> ${filtered.length}`);
        return filtered;
      });
      
      setAllBrandSegments(prev => {
        const filtered = prev.filter(seg => seg.id !== segmentId);
        console.log(`🏢 Updated brand segments list: ${prev.length} -> ${filtered.length}`);
        return filtered;
      });
      
      setProfileCounts(prev => {
        const updated = { ...prev };
        delete updated[segmentId];
        console.log(`🔢 Removed profile count for segment: ${segmentId}`);
        return updated;
      });
      
      setTotalSegments(prev => {
        const newTotal = Math.max(prev - 1, 0);
        console.log(`📊 Updated total segments: ${prev} -> ${newTotal}`);
        return newTotal;
      });
      
      // Clear selection if the deleted segment was selected
      if (selected === segmentId) {
        console.log(`🎯 Clearing selection for deleted segment: ${segmentId}`);
        setSelected(null);
      }
      
      // Also remove archive entry if any
      setArchivedItems(prev => {
        const filtered = prev.filter(a => String(a.data_id) !== String(segmentId));
        if (filtered.length !== prev.length) {
          console.log(`🗃️ Removed segment ${segmentId} from archived items`);
        }
        return filtered;
      });
      
      try { 
        await removeArchive('segment', segmentId);
        console.log(`🗃️ Successfully removed archive entry for: ${segmentId}`);
      } catch (archiveError) {
        console.warn(`⚠️ Could not remove archive entry for ${segmentId}:`, archiveError);
      }
      
      console.log(`🎉 Segment deletion completed successfully for: ${segmentId}`);
      
      setNotification({ 
        open: true, 
        message: result.message || 'Segment supprimé avec succès', 
        severity: 'success' 
      });
      
    } catch (error) {
      console.error(`❌ Error during segment deletion process for ${segmentId}:`, error);
      console.error(`❌ Error type: ${error.constructor.name}`);
      console.error(`❌ Error message: ${error.message}`);
      console.error(`❌ Error stack:`, error.stack);
      
      const errorMessage = error.message || 'Failed to delete segment - unknown error';
      
      setNotification({ 
        open: true, 
        message: `Erreur lors de la suppression du segment: ${errorMessage}`, 
        severity: 'error' 
      });
    }
  };

  // Utilities for cloning
  const slugify = (str) => {
    if (!str) return 'segment';
    return String(str)
      .normalize('NFD').replace(/\p{Diacritic}/gu, '')
      .toLowerCase().trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '') || 'segment';
  };

  const buildUniqueIdFromName = (name) => {
    const base = slugify(name);
    let candidate = base;
    let i = 2;
    const exists = (id) => allBrandSegments.some(s => String(s.id) === String(id));
    while (exists(candidate)) {
      candidate = `${base}-${i++}`;
    }
    return candidate;
  };

  const nameExistsCi = (name) => {
    const target = String(name || '').trim().toLowerCase();
    if (!target) return false;
    return allBrandSegments.some(s => {
      const n = (s.name || s.metadata?.name || '').toString().trim().toLowerCase();
      return n === target;
    });
  };

  const buildUniqueCloneName = (baseName) => {
    if (!nameExistsCi(`${baseName} (copie)`)) return `${baseName} (copie)`;
    let i = 2;
    while (nameExistsCi(`${baseName} (copie ${i})`)) i += 1;
    return `${baseName} (copie ${i})`;
  };

  const handleCloneSegment = (segmentId) => {
    // Check if actions are disabled (loading or in use)
    if (isSegmentActionsDisabled(segmentId)) {
      if (segmentId === selected && isAnalysisLoading) {
        setNotification({ 
          open: true, 
          message: "Veuillez patienter pendant l'analyse Qilinsa Amplify...", 
          severity: 'info' 
        });
      } else if (segmentCampaignsLoading && segmentId === selected) {
        setNotification({ 
          open: true, 
          message: 'Veuillez patienter pendant le chargement des informations de campagne...', 
          severity: 'info' 
        });
      } else if (isSegmentInUse(segmentId)) {
        setNotification({ 
          open: true, 
          message: 'Impossible de cloner ce segment car il est utilisé dans des campagnes actives', 
          severity: 'warning' 
        });
      }
      return;
    }

    const original = allBrandSegments.find(s => s.id === segmentId) || segments.find(s => s.id === segmentId);
    const origName = original?.name || original?.metadata?.name || 'Segment';
    setCloneSourceId(segmentId);
    setCloneName(buildUniqueCloneName(origName));
    setCloneError('');
    setCloneDialogOpen(true);
  };

  const closeCloneDialog = () => {
    setCloneDialogOpen(false);
    setCloneSourceId(null);
    setCloneName('');
    setCloneError('');
    setCloneLoading(false);
  };

  const confirmCloneSegment = async () => {
    try {
      const newName = (cloneName || '').trim();
      if (!newName) {
        setCloneError("Le nom du segment est requis");
        return;
      }
      if (nameExistsCi(newName)) {
        setCloneError("Un segment avec ce nom existe déjà. Veuillez choisir un autre nom.");
        return;
      }
      if (!cloneSourceId) {
        setCloneError("Aucun segment source sélectionné");
        return;
      }
      setCloneLoading(true);

      console.log(`🔄 Cloning segment: ${cloneSourceId} to ${newName}`);

      // Get the original segment first
      const original = allBrandSegments.find(s => s.id === cloneSourceId) || segments.find(s => s.id === cloneSourceId);

      // Use secure backend endpoint to clone the segment
      const result = await segmentService.cloneSegment(
        cloneSourceId, 
        newName, 
        `Copie de ${original?.name || cloneSourceId}`
      );
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to clone segment');
      }
      
      console.log(`✅ Successfully cloned segment. Result:`, result);
      
      // Get the cloned segment ID - try multiple sources
      const clonedSegmentId = result.segment?.id || 
                             result.segment?.metadata?.id || 
                             result.segment?.list?.id ||
                             buildUniqueIdFromName(newName);
      
      console.log(`🎯 Cloned segment ID: ${clonedSegmentId}`);

      // Close dialog first
      closeCloneDialog();
      
      // Show success notification
      setNotification({ 
        open: true, 
        message: `Segment "${newName}" cloné avec succès`, 
        severity: 'success' 
      });

      // Force refresh the entire segment list
      console.log(`🔄 Refreshing segment list to show cloned segment...`);
      
      // Clear current selection temporarily
      setSelected(null);
      
      // Refresh the data with a callback to select the cloned segment
      const refreshAndSelect = async () => {
        try {
          // Fetch fresh data from backend
          const [favs, archives] = await Promise.all([
            getFavorites('segment').catch(() => favorites),
            getArchives('segment').catch(() => archivedItems)
          ]);

          setFavorites(favs);
          setArchivedItems(archives);
          
          // Fetch segments with current filters
          const result = await fetchSegmentsWithDependencies(1, pageSize, search, activeFilter, favs, archives);
          const fullSegmentList = result.fullList;
          
          setAllBrandSegments(fullSegmentList);
          setTotalSegments(result.total);

          console.log(`📊 Full segment list after refresh: ${fullSegmentList.length} segments`);
          
          // Find the cloned segment in the refreshed list
          let clonedSegment = fullSegmentList.find(s => 
            s.id === clonedSegmentId || 
            s.metadata?.id === clonedSegmentId ||
            s.name === newName ||
            s.metadata?.name === newName
          );
          
          console.log(`🔍 Found cloned segment:`, clonedSegment);
          
          if (clonedSegment) {
            // Calculate which page the cloned segment is on
            const segmentIndex = fullSegmentList.findIndex(s => s.id === clonedSegment.id);
            const targetPage = Math.floor(segmentIndex / pageSize) + 1;
            
            console.log(`📍 Cloned segment is at index ${segmentIndex}, page ${targetPage}`);
            
            // Get the segments for the target page
            const paginatedData = fullSegmentList.slice((targetPage - 1) * pageSize, targetPage * pageSize);
            
            // Update the UI to show the correct page
            setSegments(paginatedData);
            setCurrentPage(targetPage);
            
            // CRITICAL: Cancel processes before selecting cloned segment
            cancelAllOngoingProcesses(`Auto-selected cloned segment: ${clonedSegment.id}`).catch(console.warn);
            
            // Select the cloned segment
            setSelected(clonedSegment.id);
            
            console.log(`✅ Selected cloned segment: ${clonedSegment.id} on page ${targetPage}`);
            
            // Fetch profile counts for the current page
            if (paginatedData.length > 0 && !result.hasProfileCounts) {
              await refreshProfileCounts(paginatedData);
            }
            
          } else {
            console.warn(`⚠️ Could not find cloned segment with ID ${clonedSegmentId} or name ${newName} in refreshed list`);
            
            // Fallback: just refresh to the first page
            const firstPageData = fullSegmentList.slice(0, pageSize);
            setSegments(firstPageData);
            setCurrentPage(1);
            
            if (firstPageData.length > 0) {
              await refreshProfileCounts(firstPageData);
            }
          }
          
        } catch (refreshError) {
          console.error('Error during post-clone refresh:', refreshError);
          setNotification({ 
            open: true, 
            message: 'Segment cloné mais erreur lors de l\'actualisation de la liste', 
            severity: 'warning' 
          });
        }
      };

      // Execute the refresh and selection
      await refreshAndSelect();

      // Create corresponding segment in Mautic (tag-based approach) - do this in background
      (async () => {
        try {
          const mauticConfig = {
            name: newName,
            alias: clonedSegmentId,
            description: original?.metadata?.description || original?.description || '',
            tag_name: clonedSegmentId,
          };
          const mauticResp = await createSegment('mautic', mauticConfig);
          if (mauticResp?.success) {
            const tagInfo = mauticResp?.tag ? ` (tag '${mauticResp.tag.tag}' #${mauticResp.tag.id})` : '';
            setNotification({ open: true, message: `Segment créé dans Mautic${tagInfo}`, severity: 'success' });
          } else {
            setNotification({ open: true, message: `Échec de la création du segment dans Mautic: ${mauticResp?.message || 'Erreur inconnue'}`, severity: 'warning' });
          }
        } catch (mauticErr) {
          console.error('Erreur lors de la création du segment dans Mautic:', mauticErr);
          setNotification({ open: true, message: `Erreur lors de la création du segment dans Mautic: ${mauticErr?.message || mauticErr}`, severity: 'warning' });
        }
      })();
      
    } catch (err) {
      console.error('Erreur lors du clonage du segment:', err);
      setCloneError(err?.message || 'Erreur inconnue lors du clonage');
      setNotification({ open: true, message: `Erreur lors du clonage: ${err?.message || ''}` , severity: 'error' });
      setCloneLoading(false);
    }
  };

  const handleArchiveSegment = async (segmentId) => {
    if (!segmentId) return;
    
      try {
        // Check if actions are disabled (loading or in use)
        if (isSegmentActionsDisabled(segmentId)) {
          if (segmentId === selected && isAnalysisLoading) {
            setNotification({ 
              open: true, 
              message: "Veuillez patienter pendant l'analyse Qilinsa Amplify...", 
              severity: 'info' 
            });
          } else if (segmentCampaignsLoading && segmentId === selected) {
            setNotification({ 
              open: true, 
              message: 'Veuillez patienter pendant le chargement des informations de campagne...', 
              severity: 'info' 
            });
          } else if (isSegmentInUse(segmentId)) {
            setNotification({ 
              open: true, 
              message: 'Impossible d\'archiver ce segment car il est utilisé dans des campagnes actives', 
              severity: 'warning' 
            });
          }
          return;
        }

      await addArchive('segment', segmentId);
      setArchivedItems(prev => [...prev, { data_type: 'segment', data_id: segmentId }]);
      
      // Remove from visible list immediately
      setSegments(prev => prev.filter(s => s.id !== segmentId));
      
      // Update total count
      const newTotal = Math.max(0, totalSegments - 1);
      setTotalSegments(newTotal);
      
      // If the page becomes empty and there are previous pages, go to previous page
      const remainingSegments = segments.filter(s => s.id !== segmentId);
      if (remainingSegments.length === 0 && currentPage > 1 && newTotal > 0) {
        const newPage = Math.max(1, currentPage - 1);
        setCurrentPage(newPage);
        
        // Reload the previous page
        const result = await fetchSegments(newPage, pageSize, search, activeFilter);
        setSegments(result.segments);
        
        // Update cache
        setAllBrandSegments(result.fullList);
      }
      
      if (selected === segmentId) setSelected(null);
      
      showNotification('Segment archivé avec succès', 'success');
    } catch (error) {
      console.error('Error archiving segment:', error);
      showNotification('Erreur lors de l\'archivage', 'error');
    }
  };

  const handleRestoreSegment = async (segmentId) => {
    if (!segmentId) return;
    
      try {
        // Check if actions are disabled (loading or in use)
        if (isSegmentActionsDisabled(segmentId)) {
          if (segmentId === selected && isAnalysisLoading) {
            setNotification({ 
              open: true, 
              message: "Veuillez patienter pendant l'analyse Qilinsa Amplify...", 
              severity: 'info' 
            });
          } else if (segmentCampaignsLoading && segmentId === selected) {
            setNotification({ 
              open: true, 
              message: 'Veuillez patienter pendant le chargement des informations de campagne...', 
              severity: 'info' 
            });
          } else if (isSegmentInUse(segmentId)) {
            setNotification({ 
              open: true, 
              message: 'Impossible de restaurer ce segment car il est utilisé dans des campagnes actives', 
              severity: 'warning' 
            });
          }
          return;
        }

        await removeArchive('segment', segmentId);
        setArchivedItems(prev => prev.filter(arch => String(arch.data_id) !== String(segmentId)));
      
      // If on archived filter, refresh to remove the restored item from view
      if (activeFilter === 'archived') {
        // Remove from visible list immediately
        setSegments(prev => prev.filter(s => s.id !== segmentId));
        
        // Update total count
        const newTotal = Math.max(0, totalSegments - 1);
        setTotalSegments(newTotal);
        
        // If the page becomes empty and there are previous pages, go to previous page
        const remainingSegments = segments.filter(s => s.id !== segmentId);
        if (remainingSegments.length === 0 && currentPage > 1 && newTotal > 0) {
          const newPage = Math.max(1, currentPage - 1);
          setCurrentPage(newPage);
          
          // Reload the previous page
          const result = await fetchSegments(newPage, pageSize, search, activeFilter);
          setSegments(result.segments);
          
          // Update cache
          setAllBrandSegments(result.fullList);
        }
      } else {
        // For other filters, reload current page
        const result = await fetchSegments(currentPage, pageSize, search, activeFilter);
        setSegments(result.segments);
        setTotalSegments(result.total);
        
        // Update cache
        setAllBrandSegments(result.fullList);
      }
      
      if (selected === segmentId) setSelected(null);
      
      showNotification('Segment restauré avec succès', 'success');
    } catch (error) {
      console.error('Error restoring segment:', error);
      showNotification('Erreur lors de la restauration', 'error');
    }
  };

  const handleActionMenuOpen = (event) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuOpen(true);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setActionMenuOpen(false);
  };
  
  // Delete confirmation helpers
  const openDeleteConfirm = (segmentId) => {
    setSegmentToDelete(segmentId);
    setConfirmDeleteOpen(true);
  };
  const closeDeleteConfirm = () => {
    setConfirmDeleteOpen(false);
    setSegmentToDelete(null);
    setIsDeletingSegment(false);
  };
  const confirmDelete = async () => {
    if (segmentToDelete) {
      setIsDeletingSegment(true);
      try {
      await handleDeleteSegment(segmentToDelete);
    closeDeleteConfirm();
      } catch (error) {
        // Error handling is done in handleDeleteSegment, just keep dialog open
        console.error('Error deleting segment:', error);
      } finally {
        setIsDeletingSegment(false);
      }
    }
  };

  const handleEditSegment = async () => {
    const segment = allBrandSegments.find(s => s.id === selected);
      if (segment) {
        try {
          // Check if actions are disabled (loading or in use)
          if (isSegmentActionsDisabled(selected)) {
            if (isAnalysisLoading) {
              setNotification({ 
                open: true, 
                message: "Veuillez patienter pendant l'analyse Qilinsa Amplify...", 
                severity: 'info' 
              });
            } else if (segmentCampaignsLoading) {
              setNotification({ 
                open: true, 
                message: 'Veuillez patienter pendant le chargement des informations de campagne...', 
                severity: 'info' 
              });
            } else if (isSegmentInUse(selected)) {
              setNotification({ 
                open: true, 
                message: 'Impossible de modifier ce segment car il est utilisé dans des campagnes actives', 
                severity: 'warning' 
              });
            }
            return;
          }

        // Set the selected segment for the update process
        setSelectedSegment(segment);
        
        // Initialize form fields with current segment data
        setEditSegmentName(segment.name || segment.metadata?.name || '');
      setEditSegmentDescription(segment.metadata?.description || segment.description || '');
      setEditSegmentRules('');
      
      // Generate the correct segment format for display
      const correctSegmentFormat = {
        itemId: segment.itemId || segment.id,
        itemType: segment.itemType || "segment",
        version: segment.version || 23,
        condition: segment.condition || {
          parameterValues: {
            operator: "and",
            subConditions: [
              {
                parameterValues: {
                  propertyName: "properties.brand",
                  comparisonOperator: "equals",
                  propertyValue: segment.scope || segment.metadata?.scope || "batira"
                },
                type: "profilePropertyCondition"
              },
              ...(segment.condition?.parameterValues?.subConditions || [])
            ]
          },
          type: "booleanCondition"
        },
        metadata: segment.metadata || {
          id: segment.id,
          name: segment.name,
          description: segment.description || "",
          scope: segment.scope || "batira",
          tags: segment.tags || [],
          systemTags: segment.systemTags || [],
          enabled: segment.enabled !== undefined ? segment.enabled : true,
          missingPlugins: segment.missingPlugins !== undefined ? segment.missingPlugins : false,
          hidden: segment.hidden !== undefined ? segment.hidden : false,
          readOnly: segment.readOnly !== undefined ? segment.readOnly : false
        }
      };
      setGeneratedDefinition(JSON.stringify(correctSegmentFormat, null, 2));
        
        // Optionally, fetch additional segment information from both systems
        console.log('Editing segment:', segment.name || segment.metadata?.name);
        
      setIsEditing(true);
      } catch (error) {
        console.error('Error preparing segment for editing:', error);
        setNotification({ 
          open: true, 
          message: 'Erreur lors de la préparation du segment pour modification', 
          severity: 'error' 
        });
      }
    }
    handleActionMenuClose();
  };

    const handleSaveChanges = async () => {
    try {
      // Set saving state to disable inputs and show loading
      setIsSaving(true);
      
      // Get the current segment being edited
      const currentSegment = selectedSegment;
      if (!currentSegment) {
        setNotification({ open: true, message: 'Aucun segment sélectionné pour modification', severity: 'error' });
        setIsSaving(false);
        return;
      }

      // Prepare the update request
      const updateRequest = {
        segment_name: currentSegment.name || currentSegment.metadata?.name,
        name: editSegmentName, 
        description: editSegmentDescription,
        rules: editSegmentRules
      };

      console.log('Updating segment with data:', updateRequest);

      // Show processing message
      if (editSegmentRules.trim()) {
        setNotification({ 
          open: true, 
          message: 'Traitement des règles en cours avec IA et mise à jour des segments...', 
          severity: 'info' 
        });
      } else {
        setNotification({ 
          open: true, 
          message: 'Mise à jour du segment en cours...', 
          severity: 'info' 
        });
      }

      // Call the backend update endpoint with extended timeout
      const response = await api.put('/segments/update', updateRequest, {
        timeout: 120000 // 2 minutes timeout for segment updates
      });
      
            if (response.data.success) {
        setIsEditing(false);
        
        let successMessage = 'Segment modifié avec succès!';
        if (editSegmentRules.trim()) {
          successMessage += ' Les règles ont été traitées par IA et les conditions ont été générées automatiquement.';
        }
        
        // Check for partial success (warnings)
        if (response.data.errors && response.data.errors.length > 0) {
          successMessage += ' (Quelques avertissements: ' + response.data.errors.join(', ') + ')';
        }
        
        setNotification({ 
          open: true, 
          message: successMessage, 
          severity: response.data.errors && response.data.errors.length > 0 ? 'warning' : 'success' 
        });
        
        // Refresh the segments list to show updated data while maintaining selection
        const currentSegmentId = selected;
        const result = await fetchSegments(currentPage, pageSize, search, activeFilter);
        setSegments(result.segments);
        setTotalSegments(result.total);
        
        // Update cache
        setAllBrandSegments(result.fullList);
        
        // Ensure the updated segment is still selected and update its data
        if (selectedSegment && currentSegmentId) {
          const updatedSegment = { 
            ...selectedSegment,
            name: editSegmentName,
            metadata: {
              ...selectedSegment.metadata,
              name: editSegmentName,
              description: editSegmentDescription
            }
          };
          setSelectedSegment(updatedSegment);
          
          // Update the segment in the lists to reflect changes
          setAllBrandSegments(prev => 
            prev.map(seg => 
              seg.id === currentSegmentId 
                ? { ...seg, name: editSegmentName, metadata: { ...seg.metadata, name: editSegmentName, description: editSegmentDescription } }
                : seg
            )
          );
          
          setSegments(prev => 
            prev.map(seg => 
              seg.id === currentSegmentId 
                ? { ...seg, name: editSegmentName, metadata: { ...seg.metadata, name: editSegmentName, description: editSegmentDescription } }
                : seg
            )
          );
        }
        
        // Reset saving state
        setIsSaving(false);
              } else {
          // Check if there's partial success (e.g., Mautic worked but Unomi timed out)
          const hasUnomiResult = response.data.unomi_result !== null;
          const hasMauticResult = response.data.mautic_result !== null;
          
          if (hasUnomiResult || hasMauticResult) {
            // Partial success
            let partialMessage = 'Segment partiellement modifié: ';
            if (hasUnomiResult && hasMauticResult) {
              partialMessage += 'Succès dans';
            } else if (hasUnomiResult) {
              partialMessage += 'Succès dans Unomi uniquement';
            } else if (hasMauticResult) {
              partialMessage += 'Succès dans Mautic uniquement';
            }
            
            if (response.data.errors && response.data.errors.length > 0) {
              partialMessage += '. Erreurs: ' + response.data.errors.join(', ');
            }
            
            setNotification({ 
              open: true, 
              message: partialMessage, 
              severity: 'warning' 
            });
            
            // Still refresh the segments list as something was updated
            const currentSegmentId = selected;
            const result = await fetchSegments(currentPage, pageSize, search, activeFilter);
            setSegments(result.segments);
            setTotalSegments(result.total);
            
            // Update cache
            setAllBrandSegments(result.fullList);
            
            if (selectedSegment && currentSegmentId) {
              const updatedSegment = { 
                ...selectedSegment,
                name: editSegmentName,
                metadata: {
                  ...selectedSegment.metadata,
                  name: editSegmentName,
                  description: editSegmentDescription
                }
              };
              setSelectedSegment(updatedSegment);
              
              // Update the segment in the lists to reflect changes
              setAllBrandSegments(prev => 
                prev.map(seg => 
                  seg.id === currentSegmentId 
                    ? { ...seg, name: editSegmentName, metadata: { ...seg.metadata, name: editSegmentName, description: editSegmentDescription } }
                    : seg
                )
              );
              
              setSegments(prev => 
                prev.map(seg => 
                  seg.id === currentSegmentId 
                    ? { ...seg, name: editSegmentName, metadata: { ...seg.metadata, name: editSegmentName, description: editSegmentDescription } }
                    : seg
                )
              );
            }
            
            // Reset saving state
            setIsSaving(false);
          } else {
            // Complete failure
            const errorMessage = response.data.errors 
              ? response.data.errors.join(', ')
              : response.data.message || 'Erreur lors de la modification du segment';
            setNotification({ 
              open: true, 
              message: `Erreur: ${errorMessage}`, 
              severity: 'error' 
            });
            
            // Reset saving state
            setIsSaving(false);
          }
        }
    } catch (error) {
      console.error('Error updating segment:', error);
      
      let errorMessage = 'Erreur de connexion';
      
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        errorMessage = 'La modification a pris trop de temps. Veuillez vérifier si le segment a été mis à jour et réessayer si nécessaire.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setNotification({ 
        open: true, 
        message: `Erreur lors de la modification: ${errorMessage}`, 
        severity: 'error' 
      });
      
      // Reset saving state
      setIsSaving(false);
    }
  };

  const handleSaveSegment = handleSaveChanges;

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsSaving(false);
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Helper function to fetch all campaigns with caching and cancellation support
  const fetchAllCampaigns = async (abortSignal = null) => {
    // Check if we have a valid cache
    const now = Date.now();
    if (allCampaignsCache && campaignsCacheTimestamp && (now - campaignsCacheTimestamp) < CAMPAIGNS_CACHE_DURATION) {
      console.log('Using cached campaigns data');
      return allCampaignsCache;
    }

    console.log('Fetching fresh campaigns data...');
    const allCampaigns = [];
    let page = 1;
    const pageSize = 50;
    let hasMorePages = true;

    while (hasMorePages) {
      // Check for cancellation before each request
      if (abortSignal?.aborted) {
        console.log('Campaign fetch cancelled during pagination');
        throw new Error('Campaign fetch cancelled');
      }
      
      try {
        const response = await api.get('/campaigns', {
          params: {
            page: page,
            page_size: pageSize,
          },
          signal: abortSignal // Add abort signal to axios request
        });

        const campaigns = response?.data?.campaigns || [];
        const total = response?.data?.total || 0;
        
        allCampaigns.push(...campaigns);
        
        // Check if we have more pages
        hasMorePages = campaigns.length === pageSize && allCampaigns.length < total;
        page++;
        
        // Safety check to prevent infinite loops
        if (page > 100) {
          console.warn('Reached maximum page limit (100) while fetching campaigns');
          break;
        }
      } catch (pageError) {
        console.error(`Error fetching campaigns page ${page}:`, pageError);
        hasMorePages = false;
      }
    }

    console.log(`Fetched ${allCampaigns.length} total campaigns`);
    
    // Cache the results
    setAllCampaignsCache(allCampaigns);
    setCampaignsCacheTimestamp(now);
    
    return allCampaigns;
  };

  // Optimized campaign fetching with caching and cancellation support
  const fetchSegmentCampaigns = async (segmentId, abortSignal = null) => {
    const requestId = `campaigns_${segmentId}_${Date.now()}`;
    activeCampaignRequests.current.add(requestId);
    
    console.log(`📊 Starting campaign fetch for segment: ${segmentId}`);
    setSegmentCampaignsLoading(true);
    setSegmentCampaignsError(null);
    
    try {
      // Check if request was cancelled before starting
      if (abortSignal?.aborted) {
        console.log('Campaign fetch cancelled before starting');
        return;
      }
      
      // Use cached campaigns if available (with cancellation check)
      const allCampaigns = await fetchAllCampaigns(abortSignal);

      // Try to match by ID primarily, then by segment name as fallback
      const baseSeg = allBrandSegments.find(s => s.id === segmentId);
      const segNameLc = baseSeg?.name ? String(baseSeg.name).toLowerCase() : null;

      const matchesSegment = (campaign) => {
        const lists = campaign?.lists;
        // Case 1: lists is an array (preferred)
        if (Array.isArray(lists)) {
          const matchInArray = lists.some((l) => {
            if (l && typeof l === 'object') {
              // Match by list id
              if (l.id != null && String(l.id) === String(segmentId)) return true;
              // Fallback: match by list name
              if (segNameLc && typeof l.name === 'string' && l.name.toLowerCase() === segNameLc) return true;
              return false;
            }
            // Primitive id
            return String(l) === String(segmentId);
          });
          if (matchInArray) return true;
        }

        // Case 2: lists is an object map { id: {...} }
        if (lists && !Array.isArray(lists) && typeof lists === 'object') {
          const ids = Object.keys(lists);
          if (ids.some(k => String(k) === String(segmentId))) return true;
          if (segNameLc) {
            return Object.values(lists).some(v => typeof v?.name === 'string' && v.name.toLowerCase() === segNameLc);
          }
        }

        // Fallback: scan events for addToLists/removeFromLists usage
        if (Array.isArray(campaign?.events)) {
          const usedInEvents = campaign.events.some(ev => {
            const additions = ev?.properties?.properties?.addToLists;
            const removals = ev?.properties?.properties?.removeFromLists;
            const a = Array.isArray(additions) ? additions : [];
            const r = Array.isArray(removals) ? removals : [];
            return [...a, ...r].some(val => String(val) === String(segmentId));
          });
          if (usedInEvents) return true;
        }

        return false;
      };

      const computeStatus = (c) => {
        const now = new Date();
        const publishUp = c?.publishUp ? new Date(c.publishUp) : null;
        const publishDown = c?.publishDown ? new Date(c.publishDown) : null;
        if (!c?.isPublished) return 'inactive';
        if (publishUp && now < publishUp) return 'scheduled';
        if (publishDown && now > publishDown) return 'completed';
        return 'active';
      };

      // Filter campaigns that use the selected segment
      const related = allCampaigns.filter(matchesSegment)
        .map(c => ({ ...c, status: c.status || computeStatus(c) }));

      // Final cancellation check before setting results
      if (abortSignal?.aborted) {
        console.log('Campaign fetch cancelled before setting results');
        return;
      }
      
      console.log(`Found ${related.length} campaigns using segment ${segmentId}`);
      setSegmentCampaigns(related);
    } catch (error) {
      // Don't show errors for cancelled requests
      if (error.name === 'AbortError' || error.message.includes('cancelled')) {
        console.log('Campaign fetch was cancelled');
        return;
      }
      
      console.error('Error fetching segment campaigns:', error);
      setSegmentCampaigns([]);
      setSegmentCampaignsError(error?.response?.data?.detail || error.message || 'Erreur lors du chargement des campagnes');
    } finally {
      // Clean up request tracking
      activeCampaignRequests.current.delete(requestId);
      
      // Only update loading state if request wasn't cancelled
      if (!abortSignal?.aborted) {
        setSegmentCampaignsLoading(false);
      }
      
      console.log(`📊 Campaign fetch completed for segment: ${segmentId}`);
    }
  };

  // Check if segment is currently used in active campaigns
  const isSegmentInUse = (segmentId) => {
    return segmentCampaigns.some(campaign => campaign.status === 'active');
  };

  // Check if segment actions should be disabled (during loading or if in use)
  const isSegmentActionsDisabled = (segmentId) => {
    // If this is the selected segment and analysis is still loading, disable actions
    if (segmentId === selected && isAnalysisLoading) {
      return true;
    }
    
    // If this is the selected segment and campaigns are still loading, disable actions
    if (segmentId === selected && segmentCampaignsLoading) {
      return true;
    }
    
    // If all processes have completed and segment is in use, disable actions
    if (!isAnalysisLoading && !segmentCampaignsLoading && isSegmentInUse(segmentId)) {
      return true;
    }
    
    return false;
  };

  // Get tooltip message for disabled actions
  const getDisabledActionTooltip = (segmentId, defaultMessage) => {
    if (segmentId === selected && isAnalysisLoading) {
      return "Veuillez patienter pendant l'analyse Qilinsa Amplify...";
    }
    
    if (segmentId === selected && segmentCampaignsLoading) {
      return "Veuillez patienter pendant le chargement des campagnes...";
    }
    
    if (isSegmentInUse(segmentId)) {
      return "Impossible - segment utilisé dans des campagnes actives";
    }
    
    return defaultMessage;
  };

  const handleToggleFavorite = async (segmentId) => {
    if (!favoritesInitialized) {
      console.warn('⚠️ Favorites not yet initialized, ignoring toggle request');
      setNotification({
        open: true,
        message: 'Veuillez patienter, chargement des favoris en cours...',
        severity: 'info'
      });
      return;
    }
    
    try {
      console.log(`🔄 Toggling favorite for segment: ${segmentId}`);
      
      const wasAddingFavorite = !isFavorite(segmentId);
      const willBeLastFavoriteRemoved = isFavorite(segmentId) && favorites.length === 1;
      
      let updatedFavorites;
      
      if (isFavorite(segmentId)) {
        console.log(`➖ Removing segment ${segmentId} from favorites`);
        await removeFavorite('segment', segmentId);
        updatedFavorites = favorites.filter(fav => fav.data_id !== segmentId);
        setFavorites(updatedFavorites);
        
        showNotification('Segment retiré des favoris', 'success');
      } else {
        console.log(`➕ Adding segment ${segmentId} to favorites`);
        const newFav = await addFavorite('segment', segmentId);
        updatedFavorites = [...favorites, newFav];
        setFavorites(updatedFavorites);
        
        showNotification('Segment ajouté aux favoris', 'success');
      }

      // If on favorites filter, refresh the list to reflect changes
      if (activeFilter === 'favorites') {
        // If removing the last favorite, clear the list
        if (willBeLastFavoriteRemoved) {
          console.log('🧹 Removing last favorite, clearing segments list');
          setSegments([]);
          setAllBrandSegments([]);
          setTotalSegments(0);
          setCurrentPage(1);
          setSelected(null);
          return;
        }

        try {
          console.log('🔄 Refreshing favorites view after toggle');
          setPaginationLoading(true);
          // Use the updated favorites list for filtering
          const result = await fetchSegmentsWithDependencies(1, pageSize, search, 'favorites', updatedFavorites, archivedItems);
          
          setSegments(result.segments);
          setTotalSegments(result.total);
          setCurrentPage(1);
          
          // Update cache
          setAllBrandSegments(result.fullList);
          
          console.log(`✅ Favorites view refreshed: ${result.segments.length} segments`);
          
        } catch (e) {
          console.error('❌ Error refreshing after favorite toggle:', e);
          setNotification({
            open: true,
            message: 'Erreur lors de l\'actualisation de la vue des favoris',
            severity: 'warning'
          });
        } finally {
          setPaginationLoading(false);
        }
      }
    } catch (error) {
      console.error('❌ Error updating favorites:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Erreur inconnue';
      
      setNotification({
        open: true,
        message: `Erreur lors de la mise à jour des favoris: ${errorMessage}`,
        severity: 'error'
      });
      
      // If the error suggests the favorites state is out of sync, reload them
      if (error?.response?.status === 404 || errorMessage.includes('not found')) {
        console.log('🔄 Favorites might be out of sync, reloading...');
        setFavoritesInitialized(false); // This will trigger a reload
      }
    }
  };

  // Cache management functions
  const invalidateCampaignsCache = () => {
    console.log('Invalidating campaigns cache');
    setAllCampaignsCache(null);
    setCampaignsCacheTimestamp(null);
  };

  const isCampaignsCacheValid = () => {
    const now = Date.now();
    return allCampaignsCache && campaignsCacheTimestamp && (now - campaignsCacheTimestamp) < CAMPAIGNS_CACHE_DURATION;
  };

  const handleNewSegment = () => {
    const prompt = "Crée un segment qui [donnez la description détaillée du segment]";
    navigate("/chat", { state: { prompt: prompt } });
  };

  // Fetch segment details using secure backend endpoint
  const fetchSegmentDetails = useCallback(async (segmentId) => {
    try {
      console.log(`Fetching segment details for: ${segmentId}`);
      const data = await segmentService.getSegmentById(segmentId);
      return data;
    } catch (e) {
      console.error('Error fetching segment details:', e);
      return null;
    }
  }, []);

  // Sequential loading: campaigns only load after analysis completes
  useEffect(() => {
    if (!selected) {
      setDetailedSegment(null);
      setSegmentCampaigns([]);
      setAnalysisCompleted(false);
      setShouldLoadCampaigns(false);
      return;
    }

    // Don't fetch campaigns immediately - wait for analysis completion signal
    if (shouldLoadCampaigns && analysisCompleted) {
      console.log('📊 Loading campaigns after analysis completion...');
      
      // Reset the trigger to prevent infinite loops
      setShouldLoadCampaigns(false);
      
      fetchSegmentCampaigns(selected, globalAbortController.current?.signal);
    }

    // Only fetch detailed segment data when the Configuration tab is accessed
    // This avoids unnecessary Unomi calls that can be slow
    if (tabValue === 1) {
      let active = true;
      fetchSegmentDetails(selected).then((data) => {
        if (active) setDetailedSegment(data);
      });
      return () => {
        active = false;
      };
    }
  }, [selected, shouldLoadCampaigns, analysisCompleted, tabValue, fetchSegmentDetails]);

  // Fetch segment details when Configuration tab is accessed
  useEffect(() => {
    if (selected && tabValue === 1 && !detailedSegment) {
      let active = true;
      fetchSegmentDetails(selected).then((data) => {
        if (active) setDetailedSegment(data);
      });
      return () => {
        active = false;
      };
    }
  }, [tabValue, selected, detailedSegment, fetchSegmentDetails]);
  
  // Fallback mechanism to re-sync favorites if they become out of sync
  useEffect(() => {
    // Check if favorites should be re-synced every 30 seconds when component is active
    const interval = setInterval(() => {
      if (favoritesInitialized && !favoritesLoading && !favoritesError && document.visibilityState === 'visible') {
        // Silently check if favorites are still valid by doing a quick API call
        getFavorites('segment')
          .then((latestFavorites) => {
            const currentIds = favorites.map(f => f.data_id).sort();
            const latestIds = (latestFavorites || []).map(f => f.data_id).sort();
            
            // If the favorites have changed, update them
            if (JSON.stringify(currentIds) !== JSON.stringify(latestIds)) {
              console.log('🔄 Favorites out of sync, updating...', {
                current: currentIds.length,
                latest: latestIds.length
              });
              setFavorites(latestFavorites || []);
            }
          })
          .catch((error) => {
            console.warn('⚠️ Favorites sync check failed:', error);
            // Don't update state on sync check failure to avoid disrupting user experience
          });
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [favorites, favoritesInitialized, favoritesLoading, favoritesError]);

  // Cleanup on component unmount and page navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      cancelAllOngoingProcesses('Page unload').catch(console.warn);
    };
    
    const handlePopState = () => {
      cancelAllOngoingProcesses('Navigation').catch(console.warn);
    };
    
    // Add event listeners for page navigation
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Cleanup on component unmount
    return () => {
      console.log('🧹 Segment component unmounting - cleaning up all processes');
      cancelAllOngoingProcesses('Component unmount').catch(console.warn);
      
      // Remove event listeners
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      
      // Log final favorites state for debugging
      console.log('🧹 Component unmounting with favorites state:', {
        favoritesCount: favorites.length,
        favoritesInitialized,
        favoritesError,
        favoritesLoading
      });
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Server-side pagination - segments are already filtered, sorted, and paginated
  const paginatedSegments = segments;

  // Fetch missing profile counts for visible segments - optimized with batch API
  useEffect(() => {
    const missing = paginatedSegments.filter(seg => profileCounts[seg.id] === undefined);
    if (missing.length > 0) {
      console.log(`Fetching missing profile counts for ${missing.length} segments`);
      refreshProfileCounts(missing);
    }
  }, [paginatedSegments]);

  // Reset pagination when filter changes (handled by handleFilterChange)
  // Reset to first page when sorting changes (handled by useEffect in fetchInitialData)

  const formatCount = (count) => {
    if (typeof count !== 'number') return count;
    if (count >= 1000) {
      // Use truncation (floor) instead of rounding for more accurate display
      // e.g., 1257 → 1.2K (not 1.3K)
      const truncated = Math.floor(count / 100) / 10;
      return truncated.toFixed(1) + 'K';
    }
    return count.toString();
  };

  const totalPages = Math.ceil((totalSegments || 0) / pageSize) || 1;

  // Tour steps configuration - only include steps for always-visible elements
  const tourSteps = useMemo(() => [
    {
      target: '[data-tourid="filter-sidebar-toggle"]',
      content: 'Utilisez ce bouton pour afficher ou masquer le panneau de filtres. Vous pouvez filtrer par tous les segments, favoris ou archivés.',
      placement: 'right',
      disableBeacon: true,
    },
    {
      target: '[data-tourid="sidebar-filters"]',
      content: 'Utilisez ces filtres pour afficher tous les segments, vos favoris ou les segments archivés. Cliquez sur un filtre pour l\'activer.',
      placement: 'right',
    },
    {
      target: '[data-tourid="new-segment-button"]',
      content: 'Cliquez ici pour créer un nouveau segment. Vous serez redirigé vers le chat IA pour définir votre segment en langage naturel.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="search-segments"]',
      content: 'Utilisez cette barre de recherche pour trouver rapidement un segment spécifique par son nom.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="sort-segments"]',
      content: 'Triez vos segments par date de création, nom, ou taille (nombre de profils). Utilisez la flèche pour inverser l\'ordre.',
      placement: 'bottom',
    },
    {
      target: '[data-tourid="segments-list"]',
      content: 'Voici la liste de tous vos segments. Cliquez sur un segment pour voir ses détails. Utilisez les icônes pour ajouter aux favoris, cloner, archiver ou supprimer.',
      placement: 'right',
    },
    {
      target: '[data-tourid="segment-details"]',
      content: 'Cette zone affiche les détails du segment sélectionné : analyse IA, campagnes utilisant ce segment, et configuration technique. Cliquez sur un segment dans la liste pour voir ses détails.',
      placement: 'left',
    },
    {
      target: '[data-tourid="pagination"]',
      content: 'Utilisez la pagination pour naviguer entre les pages de segments. Vous pouvez également modifier le nombre d\'éléments par page.',
      placement: 'top',
    },
  ], []);

  // Handle tour callbacks
  const handleTourCallback = useCallback((data) => {
    const { status, type, index, action } = data;
    
    console.log('Tour callback:', { status, type, index, action, step: tourSteps[index]?.target });
    
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRunTour(false);
      // Save to localStorage that user has completed the tour
      localStorage.setItem('segment-tour-completed', 'true');
    } else if (type === EVENTS.STEP_BEFORE) {
      // Ensure sidebar is open for steps that require it (should already be open from tour start)
      if (index === 1 && !isFilterSidebarOpen) {
        setIsFilterSidebarOpen(true);
      }
    } else if (type === EVENTS.TARGET_NOT_FOUND) {
      // If target not found, log it but let joyride handle it
      console.warn('Target not found for step', index, 'target:', tourSteps[index]?.target);
    }
  }, [tourSteps, isFilterSidebarOpen]);

  // Check if user has completed tour before, and offer to restart
  useEffect(() => {
    const tourCompleted = localStorage.getItem('segment-tour-completed');
    // Auto-start tour for first-time users
    if (!tourCompleted) {
      // Open sidebar before starting tour to ensure elements are in DOM
      if (!isFilterSidebarOpen) {
        setIsFilterSidebarOpen(true);
      }
      // Wait a bit longer to ensure sidebar is fully rendered
      setTimeout(() => setRunTour(true), 1500);
    }
  }, [isFilterSidebarOpen]);

  const renderDetailsContent = () => {
    // Prevent details from rendering before the list is ready
    if (loading) {
      return null;
    }

    // Inline edit panel
    if (isEditing && selected) {
      return (
        <Box sx={{ width: '100%', position: 'relative' }}>
          {/* Loading overlay */}
          {isSaving && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2
              }}
            >
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={40} sx={{ color: '#007B8A', mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                  Mise à jour en cours...
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#1F2937', fontSize: '1.25rem' }}>
              Modification du segment
            </Typography>
          </Box>

          {/* Builder */}
          <Paper sx={{ pt: 1, pl: 3, pr: 3, border: '1px solid #E5E7EB', borderRadius: 2, boxShadow: 'none' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pb: 2, borderBottom: '1px solid #E5E7EB' }}>
              <EditIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', fontSize: '1rem' }}>
                Constructeur de Segment
              </Typography>
            </Box>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Nom du segment"
                value={editSegmentName}
                onChange={(e) => setEditSegmentName(e.target.value)}
                size="small"
                sx={{ mb: 2 }}
                disabled={isSaving}
                InputProps={{
                  sx: isSaving ? { 
                    bgcolor: '#F9FAFB',
                    '& .MuiInputBase-input': { color: '#9CA3AF' }
                  } : {}
                }}
              />
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Règles du segment (en langage naturel)"
                placeholder="Ex: clients de Paris qui ont acheté le produit X dans les 30 derniers jours et ont dépensé plus de 100€. Vous pouvez aussi écrire 'désactiver ce segment' pour le désactiver."
                value={editSegmentRules}
                onChange={(e) => setEditSegmentRules(e.target.value)}
                size="small"
                disabled={isSaving}
                InputProps={{
                  sx: isSaving ? { 
                    bgcolor: '#F9FAFB',
                    '& .MuiInputBase-input': { color: '#9CA3AF' }
                  } : {}
                }}
              />
            </Box>
          </Paper>

          {/* Definition accordion */}
          <Accordion 
            expanded={isDefinitionOpen} 
            onChange={() => !isSaving && setIsDefinitionOpen(!isDefinitionOpen)} 
            disabled={isSaving}
            sx={{ 
              my: 3, 
              border: '1px solid #E5E7EB', 
              borderRadius: 2, 
              boxShadow: 'none',
              opacity: isSaving ? 0.6 : 1
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CodeIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937' }}>Définition Technique (Générée)</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Paper sx={{ bgcolor: '#F9FAFB', p: 2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'auto', maxHeight: 220, boxShadow: 'none' }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{generatedDefinition}</pre>
              </Paper>
            </AccordionDetails>
          </Accordion>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              onClick={handleCancelEdit}
              disabled={isSaving}
              sx={{
                bgcolor: '#FFFFFF',
                color: isSaving ? '#9CA3AF' : '#000000',
                textTransform: 'none',
                fontWeight: 600,
                border: '1px solid #D1D5DB',
                '&:hover': {
                  bgcolor: isSaving ? '#FFFFFF' : '#F3F4F6'
                },
                '&:disabled': {
                  bgcolor: '#F9FAFB',
                  borderColor: '#E5E7EB'
                }
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveChanges}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
              sx={{
                bgcolor: isSaving ? '#9CA3AF' : '#007B8A',
                color: '#FFFFFF',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 120,
                '&:hover': {
                  bgcolor: isSaving ? '#9CA3AF' : '#006B76'
                },
                '&:disabled': {
                  bgcolor: '#9CA3AF'
                }
              }}
            >
              {isSaving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </Box>
        </Box>
      );
    }
    // Removed highlightError handling - now using simple search

    if (!selected) {
      return (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%" gap={2}>
          <Typography variant="h6" color="textSecondary">
            Aucun segment sélectionné
          </Typography>
          <Typography color="textSecondary" sx={{ textAlign: 'center', maxWidth: '80%' }}>
            Cliquez sur un segment dans la liste pour afficher ses détails et lancer l'analyse.
          </Typography>
        </Box>
      );
    }

    // Prefer detailed data if already fetched, otherwise fall back to list data
    // Find the base segment from the complete list to ensure we always have the name.
    const baseInfo = allBrandSegments.find(s => s.id === selected);
    // Merge base info with detailed info from the API. This ensures 'name' is preserved.
    const selectedSegment = detailedSegment ? { ...baseInfo, ...detailedSegment } : baseInfo;
    if (!selectedSegment) return null;

    return (
      <Box sx={{ width: '100%' }}>
        {/* Header avec nom du segment et actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600, 
              color: '#1F2937',
              fontSize: '1.25rem'
            }}
          >
            {selectedSegment?.name}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Status indicator */}
            <Chip
              icon={isSegmentInUse(selected) ? <AlertCircleIcon /> : <CheckCircleIcon />}
              label={isSegmentInUse(selected) ? "En cours d'utilisation" : "Disponible"}
              size="small"
              sx={{
                bgcolor: isSegmentInUse(selected) ? '#FFF3CD' : '#D1ECF1',
                color: isSegmentInUse(selected) ? '#856404' : '#0C5460',
                fontSize: '0.75rem',
                '& .MuiChip-icon': {
                  fontSize: '0.875rem'
                }
              }}
            />
            
              {/* Edit button */}
              <Tooltip title={getDisabledActionTooltip(selected, "Modifier le segment")}>
                <span>
                  <IconButton
                    onClick={handleEditSegment}
                    size="small"
                    disabled={isSegmentActionsDisabled(selected)}
                    sx={{
                      color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#6B7280',
                      border: '1px solid',
                      borderColor: isSegmentActionsDisabled(selected) ? '#E5E7EB' : '#D1D5DB',
                      bgcolor: isSegmentActionsDisabled(selected) ? '#F9FAFB' : 'transparent',
                      '&:hover': {
                        bgcolor: isSegmentActionsDisabled(selected) ? '#F9FAFB' : '#F3F4F6',
                        color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#374151'
                      },
                    '&:disabled': {
                      bgcolor: '#F9FAFB',
                      borderColor: '#E5E7EB',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  <EditIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
            
            {/* Actions menu */}
            <Tooltip title={isSegmentInUse(selected) ? "Actions limitées - segment utilisé dans des campagnes actives" : "Plus d'actions"}>
              <span>
                <IconButton
                  onClick={handleActionMenuOpen}
                  size="small"
                  disabled={isSegmentInUse(selected)}
                  sx={{
                    color: isSegmentInUse(selected) ? '#9CA3AF' : '#6B7280',
                    '&:hover': {
                      bgcolor: isSegmentInUse(selected) ? 'transparent' : '#F3F4F6',
                      color: isSegmentInUse(selected) ? '#9CA3AF' : '#374151'
                    },
                    '&:disabled': {
                      color: '#9CA3AF'
                    }
                  }}
                >
                  <MoreVertIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            data-tourid="segment-tabs"
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#6B7280',
                '&.Mui-selected': {
                  color: '#007B8A',
                  fontWeight: 600,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#007B8A',
              },
            }}
          >
            <Tab label="Analyse & Performance" />
            <Tab label="Configuration" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {tabValue === 0 && (
          <Box sx={{ py: 2 }}>
            {/* Analyse IA */}
            <Paper sx={{ p: 3, mb: 3, border: '1px solid #E5E7EB', borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <AutoFixHighIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', fontSize: '1rem' }}>
                    Analyse par Qilinsa Amplify
                  </Typography>
                </Box>
                <Tooltip title="Actualiser l'analyse" placement="top" arrow>
                  <IconButton
                    onClick={handleRefreshAnalysis}
                    disabled={!selected || isAnalysisLoading}
                    size="small"
                    sx={{
                      color: (!selected || isAnalysisLoading) ? '#D1D5DB' : '#6B7280',
                      border: '1px solid',
                      borderColor: (!selected || isAnalysisLoading) ? '#E5E7EB' : '#D1D5DB',
                      bgcolor: 'transparent',
                      '&:hover': {
                        bgcolor: (!selected || isAnalysisLoading) ? 'transparent' : '#F3F4F6',
                        color: (!selected || isAnalysisLoading) ? '#D1D5DB' : '#007B8A'
                      },
                      '&:disabled': {
                        bgcolor: 'transparent',
                        borderColor: '#E5E7EB',
                        color: '#D1D5DB'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  >
                    <RefreshIcon 
                      sx={{ 
                        fontSize: 16,
                        transform: isAnalysisLoading ? 'rotate(360deg)' : 'none',
                        transition: isAnalysisLoading ? 'transform 1s linear infinite' : 'transform 0.2s ease-in-out'
                      }} 
                    />
                  </IconButton>
                </Tooltip>
              </Box>

              {isAnalysisLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Typography variant="body2" color="text.secondary">Analyse en cours...</Typography>
                </Box>
              )}

              {analysisError && !isAnalysisLoading && (
                <Typography variant="body2" color="error">{analysisError}</Typography>
              )}

              {analysisResult && !isAnalysisLoading && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    ref={analysisTextRef}
                    variant="body2"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      color: '#4B5563',
                      lineHeight: 1.6,
                      fontSize: '0.875rem',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: isAnalysisExpanded ? 'none' : 8,
                    }}
                  >
                    {analysisResult}
                  </Typography>
                  
                  {isExpandable && (
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => setIsAnalysisExpanded(!isAnalysisExpanded)}
                      endIcon={isAnalysisExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      size="small"
                      sx={{ 
                        mt: 2,
                        textTransform: 'none', 
                        fontWeight: 500,
                        color: '#007B8A',
                        position: 'relative',
                       '&:hover': {
                          bgcolor: 'rgba(0, 123, 138, 0.04)'
                        }
                      }}
                    >
                      {isAnalysisExpanded ? 'Voir moins' : 'Voir plus'}
                    </Button>
                  )}
                </Box>
              )}
            </Paper>

            {/* Section Campagnes */}
            <Paper sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <TrendingUpIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', fontSize: '1rem' }}>
                  Utilisé dans les campagnes
                  {!analysisCompleted && (
                    <Typography variant="caption" sx={{ ml: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                      (en attente de l'analyse...)
                    </Typography>
                  )}
                </Typography>

              </Box>

              {(segmentCampaignsLoading || !analysisCompleted) ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {!analysisCompleted ? "En attente de l'analyse..." : "Recherche des campagnes…"}
                  </Typography>
                </Box>
              ) : segmentCampaignsError ? (
                <Typography variant="body2" color="error">{segmentCampaignsError}</Typography>
              ) : segmentCampaigns.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {segmentCampaigns.map((campaign) => (
                    <Chip
                      key={campaign.id}
                      label={campaign.name}
                      size="small"
                      clickable
                      onClick={() => navigate(`/campagne?id=${campaign.id}`)}
                      sx={{
                        bgcolor: campaign.status === 'active' ? '#DBEAFE' : '#F3F4F6',
                        color: campaign.status === 'active' ? '#1E40AF' : '#4B5563',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: campaign.status === 'active' ? '#BFDBFE' : '#E5E7EB'
                        }
                      }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                  Aucune campagne
                </Typography>
              )}
            </Paper>
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{ py: 2 }}>
            {/* Description */}
            <Paper sx={{ p: 3, mb: 3, border: '1px solid #E5E7EB', borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <DescriptionIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', fontSize: '1rem' }}>
                  Description
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.6, color: '#4B5563' }}>
                {selectedSegment.metadata?.description || selectedSegment.description || 'Aucune description disponible'}
              </Typography>
            </Paper>

            {/* Définition Technique */}
            <Paper sx={{ p: 3, border: '1px solid #E5E7EB', borderRadius: 2, boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <CodeIcon sx={{ color: '#9CA3AF', fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937', fontSize: '1rem' }}>
                  Définition Technique
                </Typography>
              </Box>
              <Paper sx={{ 
                bgcolor: '#F9FAFB', 
                p: 2, 
                borderRadius: 1, 
                fontFamily: 'monospace', 
                fontSize: '0.75rem', 
                overflow: 'auto',
                maxHeight: 220,
                border: 'none',
                boxShadow: 'none'
              }}>
                <Box display="flex" justifyContent="flex-end">
                  <Tooltip title="Copier le JSON">
                    <IconButton
                      size="small"
                      onClick={() => {
                        const correctSegmentFormat = {
                          itemId: selectedSegment.itemId || selectedSegment.id,
                          itemType: selectedSegment.itemType || "segment",
                          version: selectedSegment.version || 23,
                          condition: selectedSegment.condition || {
                            parameterValues: {
                              operator: "and",
                              subConditions: [
                                {
                                  parameterValues: {
                                    propertyName: "properties.brand",
                                    comparisonOperator: "equals",
                                    propertyValue: selectedSegment.scope || selectedSegment.metadata?.scope || "batira"
                                  },
                                  type: "profilePropertyCondition"
                                },
                                ...(selectedSegment.condition?.parameterValues?.subConditions || [])
                              ]
                            },
                            type: "booleanCondition"
                          },
                          metadata: selectedSegment.metadata || {
                            id: selectedSegment.id,
                            name: selectedSegment.name,
                            description: selectedSegment.description || "",
                            scope: selectedSegment.scope || "batira",
                            tags: selectedSegment.tags || [],
                            systemTags: selectedSegment.systemTags || [],
                            enabled: selectedSegment.enabled !== undefined ? selectedSegment.enabled : true,
                            missingPlugins: selectedSegment.missingPlugins !== undefined ? selectedSegment.missingPlugins : false,
                            hidden: selectedSegment.hidden !== undefined ? selectedSegment.hidden : false,
                            readOnly: selectedSegment.readOnly !== undefined ? selectedSegment.readOnly : false
                          }
                        };
                        navigator.clipboard.writeText(JSON.stringify(correctSegmentFormat, null, 2));
                      }}
                      sx={{ color: '#6B7280', '&:hover': { color: '#007B8A' } }}
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                  {(() => {
                    // Generate the correct segment format for display
                    const correctSegmentFormat = {
                      itemId: selectedSegment.itemId || selectedSegment.id,
                      itemType: selectedSegment.itemType || "segment",
                      version: selectedSegment.version || 23,
                      condition: selectedSegment.condition || {
                        parameterValues: {
                          operator: "and",
                          subConditions: [
                            {
                              parameterValues: {
                                propertyName: "properties.brand",
                                comparisonOperator: "equals",
                                propertyValue: selectedSegment.scope || selectedSegment.metadata?.scope || "batira"
                              },
                              type: "profilePropertyCondition"
                            },
                            ...(selectedSegment.condition?.parameterValues?.subConditions || [])
                          ]
                        },
                        type: "booleanCondition"
                      },
                      metadata: selectedSegment.metadata || {
                        id: selectedSegment.id,
                        name: selectedSegment.name,
                        description: selectedSegment.description || "",
                        scope: selectedSegment.scope || "batira",
                        tags: selectedSegment.tags || [],
                        systemTags: selectedSegment.systemTags || [],
                        enabled: selectedSegment.enabled !== undefined ? selectedSegment.enabled : true,
                        missingPlugins: selectedSegment.missingPlugins !== undefined ? selectedSegment.missingPlugins : false,
                        hidden: selectedSegment.hidden !== undefined ? selectedSegment.hidden : false,
                        readOnly: selectedSegment.readOnly !== undefined ? selectedSegment.readOnly : false
                      }
                    };
                    return JSON.stringify(correctSegmentFormat, null, 2);
                  })()}
                </pre>
              </Paper>
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

    return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 0, mx: 3, mt: 0, mb: 3 }}>
      {/* Joyride Tour Component */}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous={true}
        showProgress={true}
        showSkipButton={true}
        callback={handleTourCallback}
        disableOverlayClose={false}
        disableScrolling={false}
        spotlightClicks={false}
        floaterProps={{
          disableAnimation: false,
        }}
        scrollOffset={20}
        styles={{
          options: {
            primaryColor: '#007B8A',
            zIndex: 10000,
          },
          tooltip: {
            borderRadius: 8,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonNext: {
            backgroundColor: '#007B8A',
            fontSize: 14,
            padding: '8px 16px',
            borderRadius: 4,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonBack: {
            color: '#007B8A',
            fontSize: 14,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
          buttonSkip: {
            color: '#666',
            fontSize: 14,
            fontFamily: 'Montserrat, Arial, sans-serif',
          },
        }}
        locale={{
          back: 'Précédent',
          close: 'Fermer',
          last: 'Terminer',
          next: 'Suivant',
          skip: 'Passer',
        }}
      />

      {/* Filter Sidebar - Conditionally rendered */}
      {isFilterSidebarOpen && (
        <Box
          data-tourid="filter-sidebar"
          sx={{
            width: 240,
            flexShrink: 0,
            bgcolor: '#F9FAFB',
            borderRight: '1px solid #E5E7EB',
            transition: 'width 0.2s',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            py: 1
          }}
        >
          <Box sx={{ p: 2, pt: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1F2937' }}>
              Filtres
            </Typography>
          </Box>

          <List dense sx={{ px: 1 }} data-tourid="sidebar-filters">
              {sidebarFilters.map((item) => {
                const isDisabled = item.id === 'favorites' && (!favoritesInitialized || favoritesError);
                
                return (
                <Tooltip 
                  key={item.id}
                  title={
                    item.id === 'favorites' && favoritesError 
                      ? `Erreur favoris: ${favoritesError}` 
                      : item.id === 'favorites' && favoritesLoading 
                        ? 'Chargement des favoris...'
                        : item.label
                  }
                  placement="right"
                >
                  <ListItem
                    button
                    onClick={() => handleFilterChange(item.id)}
                    disabled={isDisabled}
                    sx={{
                      py: 1,
                      px: 2,
                      mb: 0.5,
                      borderRadius: 1.5,
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      // Active filter gets the primary styling
                      backgroundColor: activeFilter === item.id 
                        ? '#007B8A' 
                        : 'transparent',
                      color: activeFilter === item.id
                        ? '#FFFFFF'
                        : isDisabled
                          ? '#9CA3AF'
                          : '#374151',
                      fontWeight: activeFilter === item.id ? 600 : 500,
                      boxShadow: activeFilter === item.id 
                        ? '0 2px 4px rgba(0, 123, 138, 0.2)' 
                        : 'none',
                      '&:hover': {
                        backgroundColor: isDisabled
                          ? 'transparent'
                          : activeFilter === item.id
                            ? '#006B76'
                            : item.id === 'favorites'
                              ? 'rgba(59, 130, 246, 0.05)'
                              : 'rgba(156, 163, 175, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      minWidth: 0, 
                      mr: 2, 
                      color: activeFilter === item.id 
                        ? '#FFFFFF'  // White color for active state
                        : isDisabled
                          ? '#9CA3AF'
                          : '#6B7280'
                    }}>
                      {item.id === 'favorites' && favoritesLoading ? (
                        <CircularProgress size={16} sx={{ color: 'inherit' }} />
                      ) : item.id === 'favorites' && favoritesError ? (
                        <StarBorderIcon sx={{ color: '#EF4444' }} />
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        item.id === 'favorites' && favoritesRetryCount > 0
                          ? `${item.label} (tentative ${favoritesRetryCount + 1})`
                          : item.label
                      } 
                      primaryTypographyProps={{ 
                        variant: 'body2', 
                        fontWeight: item.id === 'all' ? 600 : activeFilter === item.id ? 600 : 500 
                      }} 
                    />
                  </ListItem>
                </Tooltip>
                );
              })}
          </List>
        </Box>
      )}

      {/* Main Content Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', gap: 3, height: '100%', ml: isFilterSidebarOpen ? 3 : 0, transition: 'margin-left 0.2s' }}>
      {/* Colonne gauche : liste segments */}
      {/* Colonne centrale : liste segments */}
      <Box sx={{ width: '40%', minWidth: '320px' }}>
        <Paper 
          sx={{ 
            p: 2,
            borderRadius: 2,
            boxShadow: 'none',
            bgcolor: 'white',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box display="flex" alignItems="center" pb={1.5} mb={1.5} gap={1.5}>
            <Tooltip title={isFilterSidebarOpen ? "Replier la barre" : "Déplier la barre"} placement="bottom" arrow>
              <IconButton
                data-tourid="filter-sidebar-toggle"
                onClick={() => setIsFilterSidebarOpen(!isFilterSidebarOpen)}
                size="small"
                sx={{
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '50%',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': { bgcolor: '#F9FAFB' },
                  p: 0.75
                }}
              >
                {isFilterSidebarOpen ? <ChevronLeftIcon sx={{ fontSize: 20 }}/> : <ChevronRightIcon sx={{ fontSize: 20 }}/>}
              </IconButton>
            </Tooltip>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', color: '#1F2937' }}>
                Segments
              </Typography>
              <Tooltip title="Guide de démarrage">
                <IconButton 
                  size="small" 
                  onClick={() => {
                    // Open sidebar before starting tour to ensure elements are in DOM
                    if (!isFilterSidebarOpen) {
                      setIsFilterSidebarOpen(true);
                      // Wait for sidebar to render before starting tour
                      setTimeout(() => setRunTour(true), 500);
                    } else {
                      setRunTour(true);
                    }
                  }}
                  sx={{ 
                    color: '#007B8A',
                    '&:hover': { backgroundColor: 'rgba(0, 123, 138, 0.08)' },
                    p: 0.5,
                    ml: 1
                  }}
                >
                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {/* Favorites status indicator and reload button */}
              {favoritesError && (
                <Tooltip title={`Erreur favoris: ${favoritesError}. Cliquer pour recharger`}>
                  <IconButton 
                    onClick={() => {
                      setFavoritesInitialized(false);
                      setFavoritesRetryCount(0);
                    }}
                    size="small"
                    sx={{
                      color: '#EF4444',
                      '&:hover': {
                        color: '#DC2626',
                        bgcolor: '#FEF2F2'
                      },
                      p: 0.75
                    }}
                  >
                    <RefreshIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              
              {favoritesLoading && (
                <Tooltip title="Chargement des favoris...">
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 0.75 }}>
                    <CircularProgress size={16} sx={{ color: '#007B8A' }} />
                  </Box>
                </Tooltip>
              )}
              
              <Tooltip title="Nouveau Segment">
                <IconButton 
                  data-tourid="new-segment-button"
                  onClick={handleNewSegment}
                  size="small"
                  sx={{
                    color: '#6B7280',
                    '&:hover': {
                      color: '#007B8A',
                      bgcolor: '#F3F4F6'
                    },
                    p: 0.75
                  }}
                >
                  <AddIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Search & Sort */}
          <Box sx={{ mb: 2 }}>
            <TextField
              data-tourid="search-segments"
              fullWidth
              placeholder="Rechercher un segment..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleApplySearch();
                }
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ mr: 1, fontSize: '0.875rem', color: '#4B5563' }}>Trier par:</Typography>
              <TextField
                data-tourid="sort-segments"
                select
                size="small"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                sx={{ minWidth: 180, mr: 1 }}
              >
                <MenuItem value="created">Tous</MenuItem>
                <MenuItem value="name">Nom</MenuItem>
                <MenuItem value="profiles">
                  <Tooltip 
                    title="Trie les segments par nombre de profils. Les segments sans compte chargé sont considérés comme ayant zéro profils."
                    placement="right"
                    arrow
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>Taille</span>
                      {profileSortingPending && (
                        <CircularProgress size={12} sx={{ color: '#007B8A' }} />
                      )}
                    </Box>
                  </Tooltip>
                </MenuItem>
              </TextField>
              {profileSortingPending && sortField === 'profiles' && (
                <Typography sx={{ fontSize: '0.75rem', color: '#007B8A', fontStyle: 'italic', ml: 1 }}>
                  Chargement des tailles...
                </Typography>
              )}
            </Box>
            {/* Only show sorting arrow for name and profiles sorting */}
            {(sortField === 'name' || sortField === 'profiles') && (
              <Tooltip title={`Trier par ordre ${sortDirection === 'asc' ? 'décroissant' : 'croissant'}`}>
                <IconButton
                  size="small"
                  onClick={() => setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                  sx={{ color: '#6B7280', '&:hover': { color: '#007B8A', bgcolor: '#F3F4F6' } }}
                >
                  {sortDirection === 'asc' ? (
                    <ArrowUpwardIcon fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Segments List */}
          <Box data-tourid="segments-list" sx={{ flex: 1, overflow: 'auto' }}>
            <List dense sx={{ p: 0 }}>
              {loading || paginationLoading ? (
                <ListItem>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={16} />
                        <Typography>
                          {loading ? "Chargement des segments..." : "Changement de page..."}
                        </Typography>
                      </Box>
                    } 
                  />
                </ListItem>
              ) : error ? (
                <ListItem>
                  <ListItemText primary={<Typography color="error">{error}</Typography>} />
                </ListItem>
              ) : paginatedSegments.length === 0 ? (
                <ListItem>
                  <ListItemText primary="Aucun segment trouvé sur cette page." />
                </ListItem>
              ) : (
                paginatedSegments.map((segment) => (
                  <ListItem key={segment.id} disablePadding sx={isEditing ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
                    <ListItemButton
                      selected={selected === segment.id}
                      onClick={async () => {
                        // CRITICAL: Immediately cancel all ongoing processes before setting new selection
                        await cancelAllOngoingProcesses(`User selected segment ${segment.id}`);
                        
                        setSelected(segment.id);
                        if (searchParams.has('name')) {
                          setSearchParams({}, { replace: true });
                        }
                      }}
                      disabled={isEditing}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.5,
                        py: 1.25,
                        px: 1.5,
                        bgcolor: selected === segment.id ? '#E0F2F1' : 'transparent',
                        color: selected === segment.id ? '#007B8A' : '#4B5563',
                        fontWeight: selected === segment.id ? 600 : 400,
                        fontSize: '0.875rem',
                        '&:hover': {
                          bgcolor: selected === segment.id ? '#E0F2F1' : '#F9FAFB',
                        },
                        '& .segment-actions': {
                          opacity: 0,
                          transition: 'opacity 0.2s',
                        },
                        '&:hover .segment-actions': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                        <Tooltip 
                          title={
                            !favoritesInitialized 
                              ? "Chargement des favoris..." 
                              : favoritesError 
                                ? "Erreur de chargement des favoris" 
                                : isFavorite(segment.id) 
                                  ? "Retirer des favoris" 
                                  : "Ajouter aux favoris"
                          }
                          placement="top"
                        >
                          <IconButton
                            onClick={e => { 
                              e.stopPropagation(); 
                              handleToggleFavorite(segment.id); 
                            }}
                            size="small"
                            disabled={!favoritesInitialized}
                            sx={{ 
                              p: 0.5,
                              opacity: !favoritesInitialized ? 0.5 : 1,
                              cursor: !favoritesInitialized ? 'not-allowed' : 'pointer'
                            }}
                          >
                            {favoritesLoading ? (
                              <CircularProgress size={12} sx={{ color: '#B0B0B0' }} />
                            ) : favoritesError ? (
                              <StarBorderIcon sx={{ color: '#EF4444', fontSize: 16 }} />
                            ) : isFavorite(segment.id) ? (
                              <StarIcon sx={{ color: '#FFD700', fontSize: 16 }} />
                            ) : (
                              <StarBorderIcon sx={{ color: '#B0B0B0', fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                        
                        <Typography 
                          sx={{ 
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '0.875rem'
                          }}
                        >
                          {segment.name}
                        </Typography>

                        <Box className="segment-actions" sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1, 
                          position: 'absolute', 
                          right: 80, 
                          top: '50%', 
                          transform: 'translateY(-50%)',
                          backgroundColor: 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          padding: '2px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                        }}>
                          <Tooltip title={getDisabledActionTooltip(segment.id, "Cloner")}>
                            <span>
                              <IconButton
                                onClick={e => {
                                  e.stopPropagation();
                                  handleCloneSegment(segment.id);
                                }}
                                size="small"
                                disabled={isSegmentActionsDisabled(segment.id)}
                                sx={{ 
                                  p: 0.5, 
                                  color: isSegmentActionsDisabled(segment.id) ? '#D1D5DB' : '#B0B0B0', 
                                  '&:hover': { 
                                    color: isSegmentActionsDisabled(segment.id) ? '#D1D5DB' : '#007B8A' 
                                  },
                                  '&:disabled': {
                                    color: '#D1D5DB'
                                  }
                                }}
                              >
                                <ContentCopyIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={getDisabledActionTooltip(
                            segment.id,
                            isArchived(segment.id) ? "Restaurer" : "Archiver"
                          )}>
                            <span>
                              <IconButton
                                onClick={e => {
                                  e.stopPropagation();
                                  if (isArchived(segment.id)) {
                                    handleRestoreSegment(segment.id);
                                  } else {
                                    handleArchiveSegment(segment.id);
                                  }
                                }}
                                size="small"
                                disabled={isSegmentActionsDisabled(segment.id)}
                                sx={{ 
                                  p: 0.5, 
                                  color: isSegmentActionsDisabled(segment.id) ? '#D1D5DB' : '#B0B0B0', 
                                  '&:hover': { 
                                    color: isSegmentActionsDisabled(segment.id) 
                                      ? '#D1D5DB' 
                                      : isArchived(segment.id) ? '#10B981' : '#F59E0B' 
                                  },
                                  '&:disabled': {
                                    color: '#D1D5DB'
                                  }
                                }}
                              >
                                {isArchived(segment.id) ? (
                                  <UnarchiveIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <ArchiveIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={getDisabledActionTooltip(segment.id, "Supprimer")}>
                            <span>
                              <IconButton
                                onClick={e => {
                                  e.stopPropagation();
                                  openDeleteConfirm(segment.id);
                                }}
                                size="small"
                                disabled={isSegmentActionsDisabled(segment.id)}
                                sx={{ 
                                  p: 0.5, 
                                  color: isSegmentActionsDisabled(segment.id) ? '#D1D5DB' : '#B0B0B0', 
                                  '&:hover': { 
                                    color: isSegmentActionsDisabled(segment.id) ? '#D1D5DB' : '#EF4444' 
                                  },
                                  '&:disabled': {
                                    color: '#D1D5DB'
                                  }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                        
                        <Tooltip 
                          title={
                            profileCounts[segment.id] === undefined 
                              ? "Chargement du nombre de profils..." 
                              : profileCounts[segment.id] > 0 
                                ? `Cliquer pour voir les ${formatCount(profileCounts[segment.id])} contacts de ce segment`
                                : "Aucun contact dans ce segment"
                          }
                          placement="top"
                          arrow
                        >
                          <Chip
                            label={profileCounts[segment.id] === undefined ? (
                              <CircularProgress size={10} sx={{ color: 'inherit' }} />
                            ) : (
                              formatCount(profileCounts[segment.id] || 0)
                            )}
                            size="small"
                            clickable={profileCounts[segment.id] !== undefined}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (profileCounts[segment.id] !== undefined && profileCounts[segment.id] > 0) {
                                navigate(`/contacts?segment=${segment.id}&segmentName=${encodeURIComponent(segment.name)}`);
                              }
                            }}
                            sx={{
                              bgcolor: selected === segment.id ? '#007B8A' : '#E5E7EB',
                              color: selected === segment.id ? 'white' : '#4B5563',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              height: 20,
                              minWidth: 35,
                              cursor: profileCounts[segment.id] !== undefined && profileCounts[segment.id] > 0 ? 'pointer' : 'default',
                              '&:hover': profileCounts[segment.id] !== undefined && profileCounts[segment.id] > 0 ? {
                                bgcolor: selected === segment.id ? '#006B76' : '#D1D5DB',
                                transform: 'scale(1.05)'
                              } : {},
                              transition: 'all 0.2s ease-in-out',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))
              )}
            </List>
          </Box>

          {/* Pagination */}
          {!loading && (
            <Box 
              data-tourid="pagination"
              sx={{ 
               pt: 2, 
               display: 'flex',
               alignItems: 'center',
               justifyContent: 'center',
               position: 'relative'
             }}>
               {/* Items per page selector */}
               <TextField
                 select
                 size="small"
                 value={pageSize}
                 onChange={(e) => handlePageSizeChange(e)}
                 sx={{ mr: 2, width: 72 }}
               >
                 {PAGE_SIZE_OPTIONS.map((n) => (
                   <MenuItem key={n} value={n}>{n}</MenuItem>
                 ))}
               </TextField>
               <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                disabled={paginationLoading}
                color="primary"
                size="small"
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#6B7280',
                    '&:hover': {
                      bgcolor: '#F3F4F6',
                    },
                    '&.Mui-selected': {
                      bgcolor: '#007B8A',
                      color: 'white',
                      '&:hover': {
                        bgcolor: '#006064',
                      },
                    },
                  },
                }}
               />
               <Typography sx={{ position: 'absolute', right: 0, fontSize: '0.75rem', color: '#4B5563', fontWeight: 500 }}>
                 {totalSegments} segment{totalSegments !== 1 ? 's' : ''}
               </Typography>
             </Box>
          )}
        </Paper>
      </Box>

      {/* Colonne droite : détails segment */}
      <Box sx={{ width: '60%', height: '100%' }}>
        <Paper
          data-tourid="segment-details"
          sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: 2,
            height: '100%',
            overflowY: 'auto',
            backgroundColor: selected ? 'white' : '#f8f9fa',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: selected ? 'flex-start' : 'center',
            alignItems: selected ? 'stretch' : 'center',
            textAlign: selected ? 'inherit' : 'center'
          }}
        >
          {renderDetailsContent()}
        </Paper>
      </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={actionMenuOpen}
        onClose={handleActionMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #E5E7EB',
            borderRadius: 2,
            minWidth: 180
          }
        }}
      >
        <MenuItem
          onClick={() => {
            handleCloneSegment(selected);
            handleActionMenuClose();
          }}
          disabled={isSegmentActionsDisabled(selected)}
          sx={{ 
            gap: 1.5, 
            py: 1.5,
            color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : 'inherit',
            '&:disabled': {
              color: '#9CA3AF'
            }
          }}
        >
          <ContentCopyIcon sx={{ fontSize: 16, color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#6B7280' }} />
          <Typography variant="body2" sx={{ color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : 'inherit' }}>
            {(isAnalysisLoading || segmentCampaignsLoading) && selected === selected ? 'Cloner (en attente...)' : 'Cloner'}
          </Typography>
        </MenuItem>
        {isArchived(selected) ? (
          <MenuItem
            onClick={() => {
              handleRestoreSegment(selected);
              handleActionMenuClose();
            }}
            disabled={isSegmentActionsDisabled(selected)}
            sx={{ 
              gap: 1.5, 
              py: 1.5, 
              color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#10B981',
              '&:disabled': {
                color: '#9CA3AF'
              }
            }}
          >
            <UnarchiveIcon sx={{ fontSize: 16, color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#10B981' }} />
            <Typography variant="body2" sx={{ color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#10B981' }}>
              {(isAnalysisLoading || segmentCampaignsLoading) && selected === selected ? 'Restaurer (en attente...)' : 'Restaurer'}
            </Typography>
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              handleArchiveSegment(selected);
              handleActionMenuClose();
            }}
            disabled={isSegmentActionsDisabled(selected)}
            sx={{ 
              gap: 1.5, 
              py: 1.5, 
              color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#D97706',
              '&:disabled': {
                color: '#9CA3AF'
              }
            }}
          >
            <ArchiveIcon sx={{ fontSize: 16, color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#D97706' }} />
            <Typography variant="body2" sx={{ color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#D97706' }}>
              {(isAnalysisLoading || segmentCampaignsLoading) && selected === selected ? 'Archiver (en attente...)' : 'Archiver'}
            </Typography>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            openDeleteConfirm(selected);
            handleActionMenuClose();
          }}
          disabled={isSegmentActionsDisabled(selected)}
          sx={{ 
            gap: 1.5, 
            py: 1.5, 
            color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#DC2626',
            '&:disabled': {
              color: '#9CA3AF'
            }
          }}
        >
          <DeleteIcon sx={{ fontSize: 16, color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#DC2626' }} />
          <Typography variant="body2" sx={{ color: isSegmentActionsDisabled(selected) ? '#9CA3AF' : '#DC2626' }}>
            {(isAnalysisLoading || segmentCampaignsLoading) && selected === selected ? 'Supprimer (en attente...)' : 
             isSegmentInUse(selected) ? 'Impossible de supprimer' : 'Supprimer'}
          </Typography>
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            Modification du segment
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ space: 3 }}>
            <TextField
              fullWidth
              label="Nom du segment"
              value={editSegmentName}
              onChange={(e) => setEditSegmentName(e.target.value)}
              sx={{ mb: 3 }}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Description"
              value={editSegmentDescription}
              onChange={(e) => setEditSegmentDescription(e.target.value)}
              multiline
              rows={4}
              variant="outlined"
              helperText="Décrivez les critères et l'objectif de ce segment"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{
              bgcolor: '#FFFFFF',
              color: '#000000',
              textTransform: 'none',
              fontWeight: 600,
              border: '1px solid #D1D5DB',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleSaveSegment}
            startIcon={<CheckCircleIcon />}
            sx={{
              bgcolor: '#007B8A',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006B76' }
            }}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clone Dialog */}
      <Dialog
        open={cloneDialogOpen}
        onClose={cloneLoading ? undefined : closeCloneDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            Cloner le segment
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" sx={{ color: '#4B5563' }}>
            Entrez un nouveau nom pour le segment cloné. Le nom doit être unique.
          </Typography>
          <TextField
            label="Nom du segment"
            fullWidth
            value={cloneName}
            onChange={(e) => { setCloneName(e.target.value); setCloneError(''); }}
            disabled={cloneLoading}
            error={Boolean(cloneError)}
            helperText={cloneError || ' '}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button
            onClick={closeCloneDialog}
            disabled={cloneLoading}
            sx={{
              bgcolor: '#FFFFFF',
              color: '#000000',
              textTransform: 'none',
              fontWeight: 600,
              border: '1px solid #D1D5DB',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmCloneSegment}
            startIcon={cloneLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <ContentCopyIcon />}
            disabled={cloneLoading}
            sx={{
              bgcolor: '#007B8A',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#006B76' }
            }}
          >
            Cloner
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid #E5E7EB' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F2937' }}>
            Confirmer la suppression
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: '#4B5563' }}>
            Êtes-vous sûr de vouloir supprimer le segment "{(allBrandSegments.find(s => s.id === segmentToDelete) || segments.find(s => s.id === segmentToDelete))?.name || segmentToDelete}" ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #E5E7EB' }}>
          <Button
            onClick={closeDeleteConfirm}
            sx={{
              bgcolor: '#FFFFFF',
              color: '#000000',
              textTransform: 'none',
              fontWeight: 600,
              border: '1px solid #D1D5DB',
              '&:hover': { bgcolor: '#F3F4F6' }
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={confirmDelete}
            startIcon={isDeletingSegment ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <DeleteIcon />}
            disabled={isDeletingSegment}
            sx={{
              bgcolor: '#DC2626',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#B91C1C' },
              '&:disabled': { bgcolor: '#DC2626', opacity: 0.7 }
            }}
          >
            {isDeletingSegment ? 'Suppression...' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Segment;